// LR1-8 / LR1-10 / LR1-14 — THE INBOX (group threads), THE BELL, AND THE SPELL SHARE FLOW.
//
// Three surfaces that all hang off the room's header, kept out of LivingRoom.tsx so neither file
// becomes the thing App.tsx already is.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResumeReRead } from "./useResumeReRead";
import { sharedRoomRead } from "../lib/room-me";
import { useRefresh } from "./PullToRefresh";
import { createPortal } from "react-dom";
import { ArrowLeft, Ban, Bell, ChevronDown, DoorOpen, Flag, Paperclip, PenSquare, Pin, PinOff, Printer, Search, Send, SlidersHorizontal, Sparkles, UserMinus, UserPlus, Users, X , Pencil } from "lucide-react";
import {
  // The message cap, read from the law rather than typed here — the composer's own fallback and the
  // server's enforcement are the same number by construction (R28/R29).
  LIMITS,
  spellByline, spellPlainText, spellUseSummary, EMPTY_INBOX, EMPTY_NOTIFICATIONS, SHARE_SPELL_LABEL,
  // L2.7 — the ONE answer to what a refused community door does, so this screen and the header pair
  // cannot drift apart about it.
  communityDoorAnswer,
  INBOX_TITLE, INBOX_COMPOSE_LABEL, INBOX_CLOSE_LABEL, GROUP_MEMBERS_LABEL, currentMembers, INBOX_SEARCH_COPY, INBOX_UNREAD_FILTER, INBOX_FOLLOW_NOTE,
  INBOX_PIN_LABEL, INBOX_UNPIN_LABEL, INBOX_COMPOSE_HINT, inboxSearchEmpty, INBOX_PEOPLE_HEADING, INBOX_MESSAGES_HEADING, inboxPeopleEmpty, inboxPeopleHeld, inboxPeopleState, inboxSearchSections,
  EMPTY_FOLLOW_STRIP, COMPOSE_FOLLOW_HINT,
  // ITEM 3 — owner reach. `canBlock` is asked by both participant lists so a Block button is never
  // drawn on the house; `messageGateViolations` is what tells the compose picker whether this viewer
  // has to follow before she can write, so the picker narrows for a member and not for her seat.
  canBlock, messageGateViolations,
  // ITEM 5 (her `4`) — **THE UNBLOCK PATH.** The state machine is a LAW rather than a ternary in this
  // render, for the reason every decision in this house leaves a TSX file: a FAST gate cannot import
  // a component that holds hooks, so a four-state list written here is a four-state list nothing
  // runs. The words are the room's too — the whole of what she reads on this screen.
  blockedListState, BLOCKED_LIST_LABEL, EMPTY_BLOCKED_LIST, UNBLOCK_LABEL,
  // LR18b(b) — her groups in the circle: which threads belong there, what one wears where a person
  // wears a face, and the rail's own two words.
  circleGroups, groupMedallion, CIRCLE_STRIP_LABEL, CIRCLE_GROUP_LABEL,
  // LR18b(c) — leaving a group: the sentence that says what it costs and what survives, and the two
  // words on the confirm. Declared with the law, because this is the only place the house says it.
  leaveGroupSentence, LEAVE_GROUP_LABEL, LEAVE_GROUP_CONFIRM, LEAVE_GROUP_KEEP, LEAVE_GROUP_NOT_IN_IT,
  // LR18b(d) — her clause 4's creator question, asked here so a control is never offered where the
  // route would answer 403, plus the remove confirm's own three words and its not-in-it sentence.
  canManageGroup, removeFromGroupSentence, REMOVE_FROM_GROUP_LABEL, REMOVE_FROM_GROUP_CONFIRM,
  REMOVE_FROM_GROUP_KEEP, REMOVE_NOT_IN_GROUP,
  // LR18b — the compose's multi-select law and its words. `groupComposeViolations` is what makes her
  // clause 1's name REQUIRED, and it asks `isGroupThread` rather than counting, so this surface
  // holds no second definition of what a group is (R31).
  groupComposeViolations, groupComposeRefusal, composePicks, composeIsGroup, MAX_THREAD_MEMBERS,
  COMPOSE_MULTI_HINT, COMPOSE_GROUP_NAME_LABEL, COMPOSE_SELECTED_LABEL, COMPOSE_REMOVE_LABEL,
  COMPOSE_START_LABEL, COMPOSE_STARTING_LABEL, GROUP_NAME_MAX,
  receiptLabel, receiptMessageId,
  roomItems, roomNote, ROOM_READ_FAILED,
  // LR16 — the caps and the refusal sentences are the ROOM'S, asked here so the picker refuses
  // exactly what the server would (R15).
  messageMediaRefusal,
  // LR16-e — the confirm's sentence per kind, who may reach the bin, and what a deleted DM leaves
  // behind. All three are the shared law's, so what she is warned about, what she is allowed to do
  // and what the thread then says are one fact rather than three.
  canActOn, canDelete, deletionOutcome,
  // LAUNCH DAY — "the other person" is a law, not a `find` with a fallback that can return the
  // viewer. Her thread with David wore her own face because of that fallback.
  otherParticipant,
  // S1/S2: the room's ONE send-state law — D-5's own, governing this control as well as the
  // comment box, so a double-fire cannot mean two different things on two screens (R28/R29).
  commentSendState,
  GROUP_EDIT_LABEL,
  GROUP_PICTURE_LABEL,
  GROUP_PICTURE_PICK,
  threadImageRefusal,
  photoTypeFor, UNREADABLE_PICTURE,
  rowMediaIsEager,
} from "../livingRoom.js";
import { fileToBase64, preparePhotoForUpload, prepareAvatarForUpload } from "../photoUpload.js";
import { headerBadgeText } from "../headerIcons.js";
import {
  Avatar, ImageViewer, MentionPicker, Who, json, postJson, ago,
  useMentionPicker, LivingRoomMe, FOLLOW_EVENT, FollowTag, OwnerCrown, MemberMenuHost,
  type Identity, type Me, type Media, type Message, type Notification, type Spell, type ThreadRow,
} from "./LivingRoomShared";

import { useEscapeKey } from "./useEscapeKey";
// IT #287 — the beacon for bubbles that never paint, on the rail her 2026-09-18 ruling turned on.
import { reportClientLoss, LOSS_KIND, threadBubbleLoss } from "../lib/clientLoss";
import { MicButton } from "./MicButton";
import { MessageComposer } from "./MessageComposer";
import { MessageRow } from "./MessageRow";
import { GroupEditSheet } from "./GroupEditSheet";
// S6-1: "is she already looking at the newest?" is a law this house already owns — and R166's census
// found it had NO CALLERS while the one function every live field goes through wrote `scrollTop`
// unconditionally. This is that law reaching a surface, imported rather than re-derived (R28/R29).
import { alreadyAtLatest, anchorAgain } from "../lib/live-view";

// LR16 — what a picked-but-unsent attachment is. `data` is what crosses the wire; `url` is the local
// preview and never leaves the device. Kept apart on purpose: a preview that was also the payload is
// how a composer comes to post a `blob:` URL nobody else can read.
// S6-1: how far off the bottom still counts as "at the newest". Two pixels is 's own
// default and is right for a one-line field; in a message log a member sitting 40px short is, to
// her, at the bottom, and calling that "deliberately scrolled up" strands her above her own newest
// message. A PARAMETER rather than a second predicate (R28/R29).
const LOG_STICK_TOLERANCE = 48;

type DraftMedia = {
  id: string; kind: "image" | "video"; data: string; contentType: string;
  bytes: number; width?: number; height?: number; durationSeconds?: number; url: string;
};

// ---------------------------------------------------------------------------
// LR1-10 — THE BELL. Tapping a line lands on the exact post, comment or thread (`target`, composed
// on the server by `notificationTarget`, so the in-house list and PN1's future push payload cannot
// disagree about the destination).
// ---------------------------------------------------------------------------
export function NotificationBell({ unread, onOpen }: { unread: number; onOpen: () => void }) {
  return (
    <button type="button" className="lr-bell" onClick={onOpen} aria-label={unread ? `${unread} new notifications` : "Notifications"}>
      <Bell size={18} />
      {/* The THIRD copy of the badge text, and the one nobody found when the other two were merged.
          It answers `headerBadgeText` now, so her ruling reached this bell by the law changing
          rather than by somebody remembering this line existed (R28/R29). */}
      {headerBadgeText(unread) ? <em className="lr-bell__dot">{headerBadgeText(unread)}</em> : null}
    </button>
  );
}

export function NotificationsPanel({
  onClose, onGo,
}: { onClose: () => void; onGo: (t: Notification["target"]) => void }) {
  // R84 — `null` is "the bell could not be read", `[]` is "there is genuinely nothing" (W5-2/R39's
  // third state). Starting at `[]` is what let a dropped read draw *"Nothing yet."* over a room that
  // had been talking all day — R83's finding one surface over, on the control whose entire job is to
  // tell her something happened while she was away.
  const [items, setItems] = useState<Notification[] | null>(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    json("/api/living-room/notifications").then((d) => {
      const list = roomItems(d, "items");
      setItems(list);
      setNote(list ? "" : roomNote(d, ROOM_READ_FAILED));
      setLoading(false);
      // Opening the panel IS reading them. Unread state persists across sessions (it is a column),
      // so this is the only place that clears it.
      //
      // **AND IT ONLY FIRES ON A READ THAT LANDED, WHICH IS THE SHARPEST HALF OF THIS ROUND.** It
      // was unconditional: a panel that showed her nothing still told the server she had seen
      // everything, so a single dropped read cleared the bell permanently and the sentences behind
      // it were never shown to anybody. Reading is a claim about what she SAW.
      if (list) postJson("/api/living-room/notifications/read", {});
    });
  }, []);

  return (
    <Sheet label="Notifications" onClose={onClose} className="lr-modal--notes">
      <h2>What you missed</h2>
      {loading ? <p className="lr-note">Loading...</p> : null}
      {!loading && items === null ? <p className="lr-empty">{note || ROOM_READ_FAILED}</p> : null}
      {/* L2.7: an empty bell still opens and still says something, and the sentence is the room's
          own declaration rather than four words typed here (`EMPTY_NOTIFICATIONS`). */}
      {!loading && items !== null && !items.length ? <p className="lr-empty">{EMPTY_NOTIFICATIONS}</p> : null}
      <ul className="lr-notes">
        {(items || []).map((n) => (
          <li key={n.id} className={n.read ? "lr-note-row" : "lr-note-row is-new"}>
            <button type="button" onClick={() => { onGo(n.target); onClose(); }}>
              <span className="lr-note-row__text">{n.text}</span>
              <span className="lr-when">{ago(n.at)}</span>
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}

// A phone-first sheet that becomes a centred card on a desktop. One shell, so every overlay in this
// room has the same edges, the same close affordance and the same scroll behaviour.
//
// **IT PORTALS TO <body>, AND THAT IS THE ROUND-SEVEN FIX — STRUCTURAL, NOT PER-PANEL.**
//
// Her capture showed the header's inbox clipped under the site chrome. The cause is the trap this
// codebase has now hit THREE times (the N9-1 scrim, the LR1 composer picker, and this):
// **`.nav` carries `backdrop-filter: blur(18px)`, and a filtered element becomes a CONTAINING BLOCK
// for `position: fixed` descendants AND its own stacking context.** So a panel rendered inside the
// header was positioned against the NAV rather than the viewport, and was pinned inside the nav's
// own z-index: 20 — underneath everything painted above it. No amount of z-index ON THE PANEL can
// escape that, which is exactly why the fix is not per-panel.
//
// Portalling moves the panel out of the header's subtree entirely, so it is fixed to the viewport
// and takes `--z-header-panel` — ONE token for the whole header-spawned family (bell list, inbox,
// account menu) so they can never disagree about who sits on top again.
export function Sheet({
  label, onClose, className = "", children,
}: { label: string; onClose: () => void; className?: string; children: React.ReactNode }) {
  // R82: this room's one overlay shell keeps the house's one Escape promise. It was hand-typed here
  // for the only reason it could be — the hook was inside App.tsx, where this file cannot reach.
  useEscapeKey(onClose);
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="modal-backdrop lr-modal-backdrop" role="dialog" aria-modal="true" aria-label={label} onMouseDown={onClose}>
      <div className={`lr-modal ${className}`} onMouseDown={(e) => e.stopPropagation()}>
        <button className="lr-modal__close" type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// LR1-16 — DELETE. The sentence comes from the shared law, so what she is warned about and what the
// server actually does are the same fact (the post takes its replies, a working leaves but the saved
// copies stay, a message leaves an honest tombstone).
//
// **LR16-e MOVED IT HERE FROM `LivingRoom.tsx` AND EXPORTED IT, WHICH IS THE WHOLE OF THE MOUNT.**
// Its `itemKind` union has read `"post" | "comment" | "message"` since the day it was written, the
// delete route has always accepted `message`, `deletionOutcome` has always composed that kind's own
// sentence, and NO CALL SITE HAS EVER PASSED IT — the third arm of a three-arm control, built and
// wired to nothing (R38's trap). It moves rather than being copied for the reason LR16 gave about
// the lightbox one component over: a second confirm for the inbox is a second answer to *what does
// this house say before it destroys something of hers*, and this room already had one. `LivingRoom`
// imports `Sheet` from this file already, so nothing about the dependency direction moved.
export function ConfirmDelete({
  itemKind, id, isSpell = false, replies = 0, onClose, onDone,
}: { itemKind: "post" | "comment" | "message"; id: string; isSpell?: boolean; replies?: number; onClose: () => void; onDone: () => void }) {
  // LR13: a comment carrying replies takes them with it, and the count is IN the sentence. A confirm
  // that under-describes what it destroys is R35's defect with better manners.
  const outcome = deletionOutcome(isSpell ? "spell" : itemKind, { replies }) as { sentence: string };
  const [busy, setBusy] = useState(false);
  // R83 — R35's law is about the CONFIRM and R54/R61's is about the ANSWER, and this sheet had the
  // first and not the second. The route refuses in three real sentences it composes itself — 403
  // *"That is not yours to delete."*, 404 *"That is not here."*, and R72's 500 — and every one of
  // them was thrown away: the sheet closed and the feed reloaded, so a refused delete looked exactly
  // like a successful one until her post came back.
  const [status, setStatus] = useState("");
  return (
    <Sheet label="Delete" onClose={onClose} className="lr-modal--confirm">
      <h2>{outcome.sentence}</h2>
      {status ? <p className="lr-status">{status}</p> : null}
      <div className="lr-confirm">
        <button type="button" className="button button--ghost" onClick={onClose}>Keep it</button>
        <button type="button" className="button button--danger" disabled={busy} onClick={async () => {
          setBusy(true);
          const d = await postJson("/api/living-room/delete", { itemKind, id, isSpell });
          setBusy(false);
          // The guard is ABOVE the close (R52): nothing may report a deletion that did not happen.
          const refusal = roomNote(d);
          if (refusal) { setStatus(refusal); return; }
          onDone();
        }}>Delete</button>
      </div>
    </Sheet>
  );
}

// LR18b(c) — **THE ASK THAT MAKES A LEAVE CONTROL HONEST.** S6-5 killed this control's twin on a DM
// because it destroyed her history on one tap while reading like a navigation exit; a GROUP is the
// one shape that ruling does not cover, because blocking somebody does not take her out of a room
// with five other people in it. Two things separate this from what she killed: the SENTENCE says what
// leaving costs and what survives (a confirm that under-describes what it destroys is R35's defect
// with better manners), and the button that really does it wears `button--danger` — destruction
// dressed as destruction rather than as a door.
//
// **AND IT READS `left`, NOT `ok`.** This route answers `{ ok: true, left: false }` to a member who
// was not in that conversation, so a sheet reading the status code — or `roomNote`, which is "" for
// any `ok: true` — would close the thread and reload the list exactly as a real leave does (R39/R83
// and D-1's own delete defect, one act over).
function ConfirmLeaveGroup({
  threadId, title, onClose, onLeft,
}: { threadId: string; title: string; onClose: () => void; onLeft: () => void }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  return (
    <Sheet label={LEAVE_GROUP_LABEL} onClose={onClose} className="lr-modal--confirm">
      <h2>{leaveGroupSentence(title)}</h2>
      {status ? <p className="lr-status">{status}</p> : null}
      <div className="lr-confirm">
        <button type="button" className="button button--ghost" onClick={onClose}>{LEAVE_GROUP_KEEP}</button>
        <button type="button" className="button button--danger" disabled={busy} onClick={async () => {
          setBusy(true);
          const d = await postJson(`/api/living-room/thread/${threadId}/leave`, {});
          setBusy(false);
          // The guard is ABOVE the close (R52): nothing may report a leave that did not happen. The
          // server's own word first, and the room's floor beneath it.
          if (!d?.ok) { setStatus(roomNote(d)); return; }
          if (!d.left) { setStatus(String(d.message || LEAVE_GROUP_NOT_IN_IT)); return; }
          onLeft();
        }}>{LEAVE_GROUP_CONFIRM}</button>
      </div>
    </Sheet>
  );
}

// ITEM 5 (her `4`) — **THE BLOCKED LIST, WHICH IS THE ONLY PLACE AN UNBLOCK CAN LIVE.**
//
// Her clause is *"unblock path in the same place"*, and it cannot be a second state on the Block
// button: blocking closes the thread and takes the other member out of her room entirely (LR1 ruling
// 2), **so the control she would need to un-press leaves with the person it points at.** The inbox is
// the screen the block is performed from and the only one that survives it.
//
// **FOUR STATES, AND THE DECISION IS `blockedListState`, NOT A TERNARY HERE** (R18/R64). `null` is a
// read that has not answered and it HOLDS STILL; a failed read says so; `[]` is an ANSWER. Telling a
// member who has blocked somebody that she has blocked nobody reads, on a safety control, as the
// block having come undone.
//
// **AND IT READS THE PAYLOAD RATHER THAN THE STATUS CODE** (R39/R47). The unblock route answers 200
// with `blocked: false` — the truth AFTERWARDS, from the store — so a refusal cannot leave a row
// crossed off a screen while it still stands in the database.
function BlockedList({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const [rows, setRows] = useState<Identity[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState("");
  // D-5's law, one drawer over: the guard that decides is a REF, never state. A `useState` flag is
  // read from the render that BOUND the handler, so two taps inside one frame both send — it passes
  // every presence check there is and still writes twice.
  const sending = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    const d = await json("/api/living-room/blocked");
    const list = roomItems(d, "blocked");
    if (!list) { setFailed(true); setRows(null); return; }
    setFailed(false);
    setRows(list as Identity[]);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const unblock = async (who: Identity) => {
    if (sending.current.has(who.ref)) return;
    sending.current.add(who.ref);
    const d = await postJson("/api/living-room/block", { ref: who.ref, blocked: false });
    sending.current.delete(who.ref);
    const refusal = roomNote(d);
    if (refusal) { setStatus(refusal); return; }
    // The ANSWER, not the ask: `blocked` is what the store says is true now.
    if (d?.blocked) { setStatus(roomNote(d, ROOM_READ_FAILED)); return; }
    setStatus(String(d.message || ""));
    setRows((prev) => (prev || []).filter((r) => r.ref !== who.ref));
    // Her conversation with that member comes back the moment the block lifts, so the inbox behind
    // this sheet is stale until it is re-read.
    onChanged();
  };

  const state = blockedListState({ items: rows, failed });
  return (
    <Sheet label={BLOCKED_LIST_LABEL} onClose={onClose} className="lr-modal--blocked">
      <h2>{BLOCKED_LIST_LABEL}</h2>
      {status ? <p className="lr-status">{status}</p> : null}
      {state === "failed" ? <p className="lr-empty">{ROOM_READ_FAILED}</p> : null}
      {state === "empty" ? <p className="lr-empty">{EMPTY_BLOCKED_LIST}</p> : null}
      {state === "list" ? (
        <ul className="lr-blocked">
          {(rows || []).map((who) => (
            <li key={who.ref} className="lr-blocked__row">
              <Avatar who={who} size="sm" />
              <span className="lr-blocked__who">@{who.name}<OwnerCrown who={who} /></span>
              <button type="button" className="button button--ghost lr-blocked__undo"
                onClick={() => unblock(who)} aria-label={`${UNBLOCK_LABEL} @${who.name}`}>
                {UNBLOCK_LABEL}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Sheet>
  );
}

// LR18b(d) — **THE SAME ASK ONE SEAT OVER: TAKING SOMEBODY ELSE OUT.** It is the leave's own shape
// deliberately — a sentence naming the person and what survives, a danger-marked button, and the
// ANSWER read rather than the status code — because a remove is a member losing a room she is
// standing in, and this house says the same thing before it destroys something whoever is tapping.
function ConfirmRemoveFromGroup({
  threadId, who, onClose, onRemoved,
}: { threadId: string; who: Identity; onClose: () => void; onRemoved: (thread: any) => void }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  return (
    <Sheet label={REMOVE_FROM_GROUP_LABEL} onClose={onClose} className="lr-modal--confirm">
      <h2>{removeFromGroupSentence(who.name)}</h2>
      {status ? <p className="lr-status">{status}</p> : null}
      <div className="lr-confirm">
        <button type="button" className="button button--ghost" onClick={onClose}>{REMOVE_FROM_GROUP_KEEP}</button>
        <button type="button" className="button button--danger" disabled={busy} onClick={async () => {
          setBusy(true);
          const d = await postJson(`/api/living-room/thread/${threadId}/remove`, { ref: who.ref });
          setBusy(false);
          // The guard is ABOVE the close (R52), and `removed` rather than `ok` is the answer: this
          // route says 200 to a member who was already out, exactly as the leave does.
          if (!d?.ok) { setStatus(roomNote(d)); return; }
          if (!d.removed) { setStatus(String(d.message || REMOVE_NOT_IN_GROUP)); return; }
          onRemoved(d.thread);
        }}>{REMOVE_FROM_GROUP_CONFIRM}</button>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// LR1-8 — THE INBOX. Group threads: many participants, everyone can leave, block absolute.
// ---------------------------------------------------------------------------
// ITEM 5 — **THE INBOX IS A SCREEN OF ITS OWN NOW, AND THERE IS ONLY ONE OF IT.**
//
// It was a Sheet over whatever room she was standing in. Her order is a FULL DEDICATED SCREEN with
// an ornate frame, the title and COMPOSE in its header, a search bar, and structured rows carrying a
// face, a name, a preview, a time and an unread badge. The sheet is RETIRED rather than left beside
// this: two inboxes is two answers to every question below it (which one does a notification open,
// which one does the mail button raise, which one gets the next feature), and that is the disease
// this repo has written down under half a dozen round numbers.
//
// **NO TABS. Her ruling, and it is kept structurally**: there is no tab roster in this file to add
// to. The only narrowing is a search string and one unread filter, and both are the SAME list read
// differently by the SAME query — so a thread cannot be in one view and missing from another.
//
// The FOLLOW-STRIP her mockup carries is deliberately absent: it reads the follow graph, which is
// ITEM 7 and does not exist yet. A strip over no graph is a shell.
export function InboxPage({ me, openThreadId, goBack, note = "" }: {
  me: Me | null; openThreadId?: string | null; goBack: () => void; note?: string;
}) {
  return (
    <section className="page-shell inbox-page">
      {/* S-1: the page's own `.back-button` is GONE and that is not a capability lost — her header
          carries the chevron INSIDE the frame now, so keeping this one would stack two back doors
          on one screen, which her mockup does not draw. `goBack` is threaded down to it instead, so
          there is still exactly one way out and it is the one she designed. */}
      {/* R84: a failed read of her own seat is silent everywhere else on this screen — it takes the
          follow tags off every name and drops the message limit back to a default — so the one place
          it can be said is here, above the inbox it changes the behaviour of. */}
      {note ? <p className="lr-status">{note}</p> : null}
      <Inbox me={me} openThreadId={openThreadId} goBack={goBack} />
    </section>
  );
}

// THE PAGE AS THE ROUTER MOUNTS IT: it asks who she is here, and a 402 from that call raises the
// tier box rather than leaving a blank screen. Same belt-and-braces as `LivingRoomPage` — the door
// gate has already run in App's navigation, and this is what catches a tier that changed underneath
// a tab that was already open.
// **IT NEVER GUESSES AT ENTITLEMENT, AND THE WALKTHROUGH IS WHAT TAUGHT IT THAT.** The first version
// raised the tier box when the `isMember` prop was false — and that prop is false for the moment
// before the session comes back, so a DEEP LINK to /inbox threw the purple membership box in the
// face of a paying Apprentice. It is the deep-link flicker `LivingRoomPage` already carries the fix
// for, one page over: ASK THE SERVER. Only the 402's own `locked` raises the box.
export function InboxScreen({ onRequireMembership, goBack }: {
  // L2.7: the lock rides as the second argument, or a paid member below the room's tier lands on a
  // screen that renders nothing and says nothing (`requireMembership` runs the action for a member).
  onRequireMembership: (action: () => void, lock?: Record<string, unknown>) => void; goBack: () => void;
}) {
  const [me, setMe] = useState<Me | null>(null);
  const [note, setNote] = useState("");
  // The thread a push or a bell named. Read ONCE, off the URL this page was opened with.
  const [wanted] = useState(() => new URLSearchParams(window.location.search).get("thread"));

  useEffect(() => {
    sharedRoomRead("/api/living-room/me", json).then((d) => {
      // L2.7 — the box, carrying the server's own 402, and never an empty action (see
      // `communityDoorAnswer`: there is no branch here that does nothing).
      if (d?.locked) {
        const answer = communityDoorAnswer({ isMember: true, lock: d as Record<string, unknown> });
        if (answer.kind === "box") onRequireMembership(() => {}, answer.lock);
        return;
      }
      if (d?.ok) { setMe(d); setNote(""); return; }
      // R84: `me` staying null is silent, and it is not nothing — it is her follow tags gone and her
      // message limit guessed at. The 402 above is the one answer this screen already acts on.
      setNote(roomNote(d, ROOM_READ_FAILED));
    });
  }, [onRequireMembership]);

  return <InboxPage me={me} openThreadId={wanted} goBack={goBack} note={note} />;
}

function Inbox({ me, openThreadId, goBack }: { me: Me | null; openThreadId?: string | null; goBack: () => void }) {
  // R84: `null` until a read lands. Every conversation she has is behind this list.
  const [threads, setThreads] = useState<ThreadRow[] | null>(null);
  // The READ's sentence, kept apart from the one her ACTS write (R83's ruling on the room): a
  // refused block and an inbox that never arrived are different facts and must not erase each other.
  const [readNote, setReadNote] = useState("");
  const [q, setQ] = useState("");
  // >>> HER PEOPLE-FIRST SEARCH (2026-08-19). <<< `null` is *asked, nothing back* and `false` is
  // *asked, could not be read* — two facts, and only one of them may ever be drawn as "nobody"
  // (R18/R84). `held` is what ITEM 9's gate is keeping back, because an empty list under a name
  // that plainly exists reads as broken rather than as a rule (ComposeMessage's own ruling).
  const [people, setPeople] = useState<Identity[] | null | false>(null);
  const [peopleHeld, setPeopleHeld] = useState(0);
  const [peopleNote, setPeopleNote] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [composing, setComposing] = useState(false);
  const [open, setOpen] = useState<string>(openThreadId || "");
  // LR18b(d): `iCreated` is the whole of her clause 4's creator question on this side — a boolean and
  // never an id, because this payload's law is that it carries opaque refs and nothing else.
  // IT #167 — `hasMore`/`olderCount` ride the payload so a thread too big to send at once can SAY
  // there is history above rather than quietly pretending it starts here (her closet ruling: a
  // truncation the member is not told about is the defect, not the truncation).
  const [thread, setThread] = useState<{ id: string; title: string; participants: Identity[]; group: boolean; iCreated?: boolean; image?: string | null; messages: Message[]; hasMore?: boolean; olderCount?: number } | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [body, setBody] = useState("");
  // LR16: what she has picked and not yet sent, and which picture the lightbox is open on. `null` is
  // "closed" and 0 is "the first one" — a falsy index is a real index (R18's distinction, and the
  // reason this is not a bare number).
  const [draftMedia, setDraftMedia] = useState<DraftMedia[]>([]);
  const [viewing, setViewing] = useState<{ items: Media[]; at: number } | null>(null);
  // LR16-e: which of her messages the confirm is open on. The empty string is "closed" — a message id
  // is a bigint the server composes and is never falsy.
  const [deleting, setDeleting] = useState("");
  const [status, setStatus] = useState("");
  const [adding, setAdding] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // ITEM 5: the group member list is folded by default — her order is that the top of the thread
  // shows the group name and this one control, and nothing else.
  const [membersOpen, setMembersOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState<Identity | null>(null);
  // ITEM 5 (her `4`): her blocked list, which is where the unblock path lives.
  const [blockedOpen, setBlockedOpen] = useState(false);
  const picker = useMentionPicker(open || null, Boolean(me?.me?.owner));
  const boxRef = useRef<HTMLTextAreaElement>(null);

  // ---------------------------------------------------------------------------
  // C4 — **HER REPORTED FREEZE, AND IT WAS QUADRATIC IN THE THREAD.**
  //
  // Her report: the composer freezes every time she types in Elite 2026. Measured against the live
  // table before a line was written (R25/R180): **that thread holds 618 messages and 274 live
  // members — the biggest on this site.** Every keystroke calls `setBody`, which re-renders this
  // component, which maps every message to a `MessageRow`; and `receiptMessageId(thread.messages)`
  // — which walks the WHOLE thread backwards — was called INSIDE that map, once per row. **618 walks
  // of up to 618 messages: up to 381,924 array steps per keystroke**, on a value that is the same
  // for every row.
  //
  // It does not depend on the message, so resolving it ONCE is byte-identical in what renders and
  // takes the render from n-squared to n. It is `useMemo`'d on the messages themselves rather than
  // merely hoisted, so a keystroke that changes only the draft does not walk the thread at all.
  //
  // **WHAT THIS DOES NOT CLAIM:** the render is still one `MessageRow` per message per keystroke,
  // which is linear and real. That half is named rather than half-fixed (DECISIONS C4-2).
  // ---------------------------------------------------------------------------
  const receiptAt = useMemo(() => receiptMessageId(thread?.messages || []), [thread?.messages]);

  // PERF-1 — the three handlers that were inline arrows at the message map. See the note there.
  const askDelete = useCallback((id: string) => setDeleting(id), []);
  const openMedia = useCallback(
    (m: { media?: Array<Record<string, unknown>> }, at: number) => setViewing({ items: (m.media || []) as never, at }),
    []);
  const openPost = useCallback((id: string) => { window.location.href = `/living-room?post=${id}`; }, []);

  // ONE READ FOR THE WHOLE SCREEN, carrying the search and the filter, so the list she is looking at
  // is always something the SERVER decided rather than a client-side narrowing of a stale list.
  //
  // **AND IT IS SEQUENCED, WHICH THE WALKTHROUGH IS WHAT CAUGHT.** Typing three letters fires three
  // requests and nothing says they come back in order — the first version showed the answer to "N"
  // over the answer to "Nia", so the list disagreed with the box she was looking at. A counter, and
  // a stale reply is dropped rather than rendered. The @picker's own debounce is here for the other
  // half of it: a request per keystroke is a request per keystroke.
  const seq = useRef(0);
  const timer = useRef<number | null>(null);
  const loadList = useCallback(async (term = q, unread = unreadOnly) => {
    const mine = ++seq.current;
    const qs = new URLSearchParams();
    if (term.trim()) qs.set("q", term.trim());
    if (unread) qs.set("unread", "1");
    const d = await json(`/api/living-room/inbox${qs.toString() ? `?${qs}` : ""}`);
    if (mine !== seq.current) return;
    // R84: the ANSWER decides and the guard is above the paint (R52). A list that did not land is
    // `null` and says why in the server's own words; it can never be drawn as an empty inbox, and it
    // can never be drawn as a search that found nothing — which names a term SHE typed as the reason.
    const list = roomItems(d, "threads");
    setThreads(list);
    setReadNote(list ? "" : roomNote(d, ROOM_READ_FAILED));
  }, [q, unreadOnly]);
  // ITS OWN COUNTER, because the two reads answer at different speeds and the newest PEOPLE answer
  // has nothing to say about which THREADS answer is newest (R216's law, per read).
  const peopleSeq = useRef(0);
  const loadPeople = useCallback(async (term = q) => {
    const mine = ++peopleSeq.current;
    // PEOPLE STAY BEHIND A TERM — the members route's own ruling. An inbox that opened onto a
    // directory would be that ruling broken on the surface it was written for.
    if (!term.trim()) { setPeople(null); setPeopleHeld(0); setPeopleNote(""); return; }
    const d = await json(`/api/living-room/mentions?q=${encodeURIComponent(term.trim())}`);
    if (mine !== peopleSeq.current) return;
    const found = roomItems(d, "people") as Identity[] | null;
    if (!found) { setPeople(false); setPeopleHeld(0); setPeopleNote(roomNote(d, ROOM_READ_FAILED)); return; }
    // R15: a list offering somebody the draft door would then refuse costs her the action. The same
    // filter the compose picker runs, never a second opinion about who she may write to (R28/R29).
    const mineOnly = found.filter((x) => !pickerRefuses(me?.me ?? null, x));
    setPeople(mineOnly);
    setPeopleHeld(found.length - mineOnly.length);
    setPeopleNote("");
  }, [q, me?.me?.ref, me?.me?.owner]);
  const search = useCallback((term: string, unread: boolean) => {
    if (timer.current) window.clearTimeout(timer.current);
    // ONE DEBOUNCE FOR ONE KEYSTROKE. Two timers is two answers to *when does a search happen*, and
    // the one that drifts is the one she is looking at (R28/R29).
    timer.current = window.setTimeout(() => { void loadList(term, unread); void loadPeople(term); }, 140);
  }, [loadList, loadPeople]);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  // ITEM 6: the menu hands back a member REF, and the inbox opens (or creates) the conversation with
  // her — the same route the room uses, so there is one answer to what Message does.
  const openMemberThread = useCallback(async (ref: string) => {
    if (!ref) return;
    const d = await postJson("/api/living-room/thread", { ref });
    if (d?.ok && d.threadId) openThread(String(d.threadId));
  }, []);

  const openThread = useCallback(async (id: string) => {
    setOpen(id);
    const d = await json(`/api/living-room/thread/${id}`);
    if (d?.ok) setThread(d.thread);
    // R84: the floor was *"That conversation is gone."* — a CLAIM ABOUT A FACT THIS SIDE DOES NOT
    // KNOW (R64/R70: not a missing sentence but a wrong one). A dropped request said her
    // conversation had been deleted. The server's own word still comes first, and where it has none
    // the honest floor is that the read did not land.
    else { setStatus(roomNote(d, ROOM_READ_FAILED)); setOpen(""); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // **QC-3 — THE INBOX JOINS THE PULL REGISTRY.** P2-7 built the gesture, the physics and the
  // registry, and its own header states the law it was built around: a surface JOINS by registering
  // a refresh, "or the capability lives on whichever screen needed it first and every other one
  // silently does nothing on release." Measured (R25): the feed, the bell and My Room had joined and
  // THE MESSAGES INBOX NEVER HAD — which is her report exactly. It fell through to the whole-document
  // reload `needsFullReload` exists for, so the motion played and the screen came back the same.
  //
  // It refreshes WHAT SHE IS LOOKING AT: the open conversation when one is open, the list otherwise.
  // A pull inside a thread that silently re-read the list behind it would be the gesture answering a
  // question she did not ask.
  useRefresh("inbox", async () => {
    if (thread?.id) await openThread(thread.id);
    else await loadList();
  });

  // >>> **ITEM 3 — AND IT READS AGAIN WHEN SHE COMES BACK TO THE APP, WHICH IS THE CAUSE NOBODY HAD
  // LOOKED FOR.** <<< (her order on mommymoney888, 2026-09-02)
  //
  // *"Her Elite chat is STILL BLANK and SHE HAS BEEN TOLD TWICE IT WAS FIXED."* Traced on her own
  // seat, her own device and production: her tier is VET on a live subscription, `room:living` reads
  // TRUE, the server answers 200 with 150 messages and 280 participants, and the room renders
  // perfectly in a browser. **Every instrument said fine. She was still right** — which is word for
  // word what `App.tsx` already recorded about the daily guidance, on the same device class.
  //
  // Her push endpoints are `web.push.apple.com`, which on iOS means the site is **on her home
  // screen**. That app is resumed from the background for days without this tree ever unmounting, so
  // the thread payload she is looking at is the one that arrived the last time it mounted — and if
  // that read landed during an outage, **no deploy since could touch it.** A pull-to-refresh does not
  // reload the document in a standalone app; signing out and back in does not remount this
  // component. Only force-quitting did, and nobody thinks to ask that.
  //
  // **IT REFRESHES WHAT SHE IS LOOKING AT**, exactly as the pull does one line up and for the same
  // stated reason — a resume that silently re-read the list behind an open conversation would be the
  // app answering a question she did not ask. The throttle and the two events are
  // `src/lib/resumeRead.ts`'s, so this surface and the daily guidance cannot hold two answers to
  // *is this read stale*.
  useResumeReRead(() => {
    if (thread?.id) void openThread(thread.id);
    else void loadList();
  });
  useEffect(() => { if (openThreadId) openThread(openThreadId); }, [openThreadId, openThread]);

  // >>> **IT #287 — WHEN A THREAD DRAWS ITS ROWS AND NOT ONE BUBBLE PAINTS, THE HOUSE IS TOLD.** <<<
  // Arielle's class (#177/#187/#287): author rows render, every bubble invisible, on her iPhone and
  // iPad, while the server's read lands 200 — so no server instrument can ever see it, and her own
  // screenshots were the only witness. The decision is `threadBubbleLoss`, a pure law the gate
  // drives; it fires ONLY on total silence (an open thread always paints bubbles at the bottom, so
  // rows skipped off screen can never trip it). Measured after paint, once per thread open, capped
  // by the rail's own three-per-page, and it never throws into the screen it measures.
  useEffect(() => {
    const count = thread?.messages.length || 0;
    if (!thread?.id || !count) return;
    const timer = window.setTimeout(() => {
      try {
        const bubbles = document.querySelectorAll(".lr-messages .lr-msg__bubble");
        let visible = 0;
        bubbles.forEach((b) => {
          const r = b.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) visible += 1;
        });
        const detail = threadBubbleLoss({ messages: count, bubbles: bubbles.length, visible });
        if (detail) reportClientLoss(LOSS_KIND.threadBubbles, { detail: `${detail}; thread ${thread.id}` });
      } catch { /* the reporter never breaks the screen it measures */ }
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [thread?.id, thread?.messages.length]);

  // LR16 — **A MESSAGE MAY BE PICTURES ALONE**, which is how anybody sends a photograph, so the send
  // is gated on having SOMETHING rather than on having words. Nothing else moved.
  // S1/S2/S3, HER RULING: **NO THREE-SECOND DEAD AIR, AND A DOUBLE-FIRE IS STRUCTURALLY ONE
  // MESSAGE.** Her words: *"messages render OPTIMISTICALLY, the instant she hits enter or send, the
  // message appears in the thread (pending state until server-confirmed), the Instagram standard
  // already ruled for comments and evidently not shipped to DMs."*
  //
  // **MEASURED, AND BOTH HALVES WERE MISSING HERE (R25):** this send awaited the POST and only then
  // touched the thread, so her words sat in the box until the round trip came back; and it carried
  // NO in-flight guard of any kind, so D-5's comment defect — three Enter presses inside one frame,
  // three messages — was live on DMs the whole time D-5 was green on comments.
  //
  // **THE GUARD IS D-5's OWN LAW AND NOT A SECOND ONE** (R28/R29). `commentSendState` keeps its name
  // deliberately: it is the room's ONE send-state law and it governs both controls now, and renaming
  // it would rot D-5's fixture and mutation anchors for a word (R9/R104/R173 — logged in DECISIONS
  // S1-2). **THE DECIDER IS A `useRef`, WHICH IS D-5's WHOLE FINDING:** a `useState` guard reads the
  // value from the render that BOUND the handler, so three presses inside one frame all see `false`
  // and all three send — and it passes every presence check there is while doing it. The state
  // survives as what the button WEARS.
  const inFlight = useRef(false);
  const [sending, setSending] = useState(false);

  // S6-1, HER RULING: **THE THREAD SCROLLS TO THE NEW MESSAGE, INSTANTLY.** *"Sending a message
  // scrolls the thread to it INSTANTLY, the new message visible above the composer the moment it
  // renders, never hidden behind the input or below the fold, no manual scrolling ever. Same on
  // receiving while the thread is open (new arrivals scroll into view unless she has deliberately
  // scrolled up reading history, the standard messenger behaviour)."*
  //
  // **THE EXCEPTION IS THE WHOLE DESIGN, AND IT IS A FACT ABOUT WHERE SHE IS RATHER THAN A FLAG.**
  // `src/lib/live-view.ts` already owns this law for the live fields — its own comment is that it
  // *"keeps a member who has deliberately scrolled UP to re-read the top of their own message from
  // being yanked back down mid-read"* — and R166's census found that `alreadyAtLatest` had NO
  // CALLERS while `applyLiveView` wrote `scrollTop` unconditionally: the protection was written and
  // enforced nowhere. This is that law finally reaching a surface, so it is IMPORTED rather than
  // re-derived here (R28/R29 — a second definition of "is she at the bottom" is how the two would
  // come to disagree about her history).
  //
  // **A SEND ALWAYS SCROLLS, WHATEVER SHE WAS READING** — that is the difference between the two
  // halves of her ruling. She just pressed the button; the message she is looking for is her own.
  const logRef = useRef<HTMLDivElement | null>(null);
  const stickToLatest = useRef(true);
  const onLogScroll = () => {
    const el = logRef.current;
    if (el) stickToLatest.current = alreadyAtLatest(el, LOG_STICK_TOLERANCE);
  };
  const toLatest = (force = false) => {
    const el = logRef.current;
    if (!el || (!force && !stickToLatest.current)) return;
    stickToLatest.current = true;
    // W7-2 measured two passes on the Ask Lala dock: an image or a video in the row that just
    // arrived has NO HEIGHT until it loads, so a single pass lands short of the message it was aimed
    // at. #133 measured that two is not enough once PERF-1 put `content-visibility` on every row —
    // in an 872-row thread the height is an estimate that keeps moving, and taller-than-placeholder
    // rows left the anchor 1,386px SHORT with the newest message off screen. `anchorAgain` keeps
    // re-anchoring while the height is still MOVING, bounded, and stops the instant she scrolls up.
    let pass = 0;
    let lastHeight = -1;
    const step = () => {
      const e = logRef.current;
      if (!e) return;
      const height = e.scrollHeight;
      if (!anchorAgain({ pass, height, lastHeight, stuck: stickToLatest.current })) return;
      e.scrollTop = height;
      lastHeight = height;
      pass += 1;
      requestAnimationFrame(step);
    };
    step();
  };
  // ON ARRIVAL: every time the rendered list changes — her own send, a poll, somebody else's message
  // — unless she has scrolled up. The message COUNT is the trigger rather than the thread object,
  // because a re-read that changed nothing must not yank her back down.
  useEffect(() => { toLatest(); }, [thread?.messages.length]);
  // ON OPENING A THREAD: at the newest message, which is where a conversation starts being read.
  useEffect(() => { stickToLatest.current = true; toLatest(true); }, [open]);
  const send = async () => {
    if (!open) return;
    if (commentSendState({ inFlight: inFlight.current, text: body, pictures: draftMedia.length }) !== "send") return;
    inFlight.current = true;
    setSending(true);
    // **THE OPTIMISTIC ROW.** Her message is on screen before the request leaves, marked pending, and
    // it is a LOCAL row rather than a write to anything: the server's own answer replaces the whole
    // thread on confirm, so there is no merge to get wrong and no second definition of what a
    // message is (R28/R29). The id is namespaced so it can never collide with a real bigint.
    const sentBody = body;
    const sentMedia = draftMedia;
    const pending: Message = {
      id: `pending-${Date.now()}`,
      mine: true,
      body: sentBody,
      createdAt: new Date().toISOString(),
      // A COMPLETE Identity or nothing — never a subset. R139's own finding is that a hand-copied
      // subset which quietly loses a field does not throw, does not log, and renders a perfectly
      // good row about the wrong thing; `tsc` caught this one before it could. The fallback is only
      // reachable before her own seat has loaded, and a pending row is always `mine`, so the room
      // never draws this author (it prints one only for somebody ELSE in a GROUP).
      author: me?.me ?? { ref: "", name: "", avatar: "", owner: false, crowned: false },
      media: sentMedia.map((m) => ({ id: m.id, kind: m.kind, url: m.url, contentType: m.contentType })),
      likes: 0,
      liked: false,
      pending: true,
    };
    setThread((t) => (t ? { ...t, messages: [...t.messages, pending] } : t));
    // S6-1: her own send scrolls unconditionally. She just pressed the button — the message she is
    // looking for is hers, so the scrolled-up exception does not apply to it.
    stickToLatest.current = true;
    // The box clears WITH the optimistic row, because that is what "it appears in the thread" means
    // to the person typing. R57's law is kept by the refusal branch below, which puts her words back.
    setBody("");
    setDraftMedia([]);
    try {
      const d = await postJson(`/api/living-room/thread/${open}/message`, {
        body: sentBody, media: sentMedia.map((m) => ({ kind: m.kind, data: m.data, contentType: m.contentType, width: m.width, height: m.height, durationSeconds: m.durationSeconds })),
      });
      // R39/R47/R53: the ANSWER is read, never the status code. A refused send must never cost her
      // her words or the pictures she picked — retyping a message and re-choosing four photographs is
      // the whole cost of getting this wrong, and it is what the optimistic row makes possible to
      // lose. So a refusal takes the pending row back OUT and puts her words back in the box.
      if (!d?.ok) {
        setThread((t) => (t ? { ...t, messages: t.messages.filter((m) => m.id !== pending.id) } : t));
        setBody(sentBody);
        setDraftMedia(sentMedia);
        setStatus(d?.message || "That did not send.");
        return;
      }
      // Held or clean, identical: the server's thread replaces ours and the pending row is gone with
      // it. She is told nothing, because there is nothing in `d` that says anything.
      setThread(d.thread);
    } finally {
      // A stuck flag is a permanently dead Send with her words in it — D-5's own reason for putting
      // the put-back in a `finally` rather than after the answer.
      inFlight.current = false;
      setSending(false);
    }
  };

  // LR16(a) — THE COMPOSER'S PICKER. The resize is the house's shared `preparePhotoForUpload` (the
  // same path the room composer and the comment picker already use), so a 12MB phone photograph is
  // sent at the vendor long-edge rather than refused for being what a phone produces. A VIDEO is
  // NOT resized — there is no browser-side transcode worth having — so it is offered raw and the
  // caps decide.
  const pickMedia = async (files: FileList | null) => {
    if (!files?.length) return;
    const next: DraftMedia[] = [];
    for (const file of Array.from(files)) {
      const isVideo = file.type.startsWith("video/");
      if (isVideo) {
        const data = await fileToBase64(file);
        next.push({ id: `${file.name}-${file.size}-${next.length}`, kind: "video", data, contentType: file.type || "video/mp4", bytes: file.size, url: URL.createObjectURL(file) });
      } else {
        const prepared = await preparePhotoForUpload(file);
        next.push({
          id: `${file.name}-${file.size}-${next.length}`, kind: "image", data: prepared.base64,
          contentType: file.type || "image/jpeg", bytes: prepared.bytes, width: prepared.width, height: prepared.height,
          url: prepared.dataUrl,
        });
      }
    }
    const proposed = [...draftMedia, ...next];
    // **THE PICKER REFUSES WHAT THE SERVER WOULD REFUSE, AND THE TWO ASK THE SAME FUNCTION** (R15:
    // a picker that offers what the gate refuses costs her the whole send before she is told no).
    const refusal = messageMediaRefusal(proposed);
    if (refusal) { setStatus(refusal); return; }
    setStatus("");
    setDraftMedia(proposed);
  };

  // >>> PERF-1 — **THIS ONE IS WHY THE MEMO ON `MessageRow` MEASURED AS NOTHING.** <<< Written as a
  // plain arrow in this component's body it was a NEW IDENTITY ON EVERY RENDER, handed to all 698
  // rows in Elite 2026, so `memo`'s shallow compare found them unequal every keystroke and skipped
  // nobody. **The wrapper was there and it was doing NOTHING** — measured: 1,163ms per keystroke
  // without it, 1,177ms with it. A repair that reads as complete and changes nothing, which is this
  // house's most-paid shape arriving as a performance fix. Every handler this row receives has to be
  // stable or none of them matter, and that is what `perf1-render-budget.mjs` holds.
  //
  // **THE FUNCTIONAL UPDATER IS WHAT MAKES THE DEPENDENCY LIST EMPTY, AND IT IS ALSO MORE CORRECT.**
  // Reading `thread` from the closure meant acting on the thread as it was when the request STARTED
  // — a like that landed while a new message arrived wrote the old list back over it. `setThread`
  // and `postJson` are both stable, so this depends on nothing at all.
  const like = useCallback(async (m: Message) => {
    const d = await postJson("/api/living-room/like", { messageId: m.id });
    if (!d?.ok) return;
    setThread((prev) => (prev
      ? { ...prev, messages: prev.messages.map((x) => (x.id === m.id ? { ...x, liked: d.liked, likes: d.likes } : x)) }
      : prev));
  }, []);

  // R84 — **THE SHARPEST ACT IN THIS FILE, BECAUSE IT IS THE SAFETY CONTROL.** A refused block said
  // NOTHING AT ALL: the sheet stayed exactly where it was, the conversation stayed open, and she was
  // left to decide whether the tap had registered. The server composes a real refusal here
  // (`MEMBER_NOT_HERE` on a ref it cannot resolve) and it had never once reached a member.
  const block = async (ref: string) => {
    const d = await postJson("/api/living-room/block", { ref, blocked: true });
    const refusal = roomNote(d);
    if (refusal) { setStatus(refusal); return; }
    setOpen(""); setThread(null); setStatus(d.message); loadList();
  };

  // R84: leaving CLOSED THE THREAD AND RELOADED THE LIST REGARDLESS, so a refusal looked exactly
  // like a success until the conversation came back — R83's delete defect one surface over. The
  // route answers `ok: true` even when she was not in it, with its own sentence for that, so the
  // guard is about a refusal rather than about `left`.

  // HER PIN, and the payload it answers with IS the reordered list — the client never guesses where
  // the row moved to (R39: read the payload, not the status code).
  // R84: PRESENCE, NOT TRUTHINESS (R53). This was already honest about a refusal and still forged
  // `d.threads || []` on the way through, so a 200 that carried no list at all emptied her inbox.
  const pin = async (t: ThreadRow, pinned: boolean) => {
    const d = await postJson(`/api/living-room/thread/${t.id}/pin`, { pinned });
    const moved = roomItems(d, "threads");
    if (moved) setThreads(moved);
    else setStatus(roomNote(d, "That did not save."));
  };

  return (
    // ITEM 7: the inbox is a member surface, so it says who is looking and every @name it draws —
    // in a row, in a thread, in COMPOSE — gets the follow tag from `Who` without a prop anywhere.
    <LivingRoomMe.Provider value={me?.me?.ref ?? null}>
    {/* ITEM 6: mounted ONCE for the inbox — the same menu the room uses, same two actions. */}
    <MemberMenuHost onMessage={openMemberThread} />
    <div className="inbox-frame">
      {/* S-1 — HER FOUR CORNER FLOURISHES. Four spans rather than more pseudo-elements because the
          frame's own ::before and ::after are already the inner rule and the gem, and a corner is
          decoration with no text: `aria-hidden` keeps all four out of the accessibility tree. */}
      <span className="inbox-frame__corner inbox-frame__corner--tl" aria-hidden="true" />
      <span className="inbox-frame__corner inbox-frame__corner--tr" aria-hidden="true" />
      <span className="inbox-frame__corner inbox-frame__corner--bl" aria-hidden="true" />
      <span className="inbox-frame__corner inbox-frame__corner--br" aria-hidden="true" />
      {!open || !thread ? (
        <>
          {/* **HER HEADER, THREE PARTS, AND THE TITLE IS THE MIDDLE ONE.** Back chevron left, the
              serif title CENTRED, compose as an ICON right. It was a left-aligned title beside a
              gold "New message" button, which is the same three capabilities wearing none of her
              design. The compose label survives as the accessible name — the catalog row is still
              READ (14-B's law), it is simply not painted, which is what her mockup shows. */}
          <header className="inbox-head">
            {/* ITEM 1 — **THE CLOSE, AND IT IS ONE CONTROL RATHER THAN TWO.** The slot held a
                chevron labelled "Back" wired to a hard-typed Living Room; her report is the honest
                reading of that. The X returns her to the screen she opened the inbox from, and the
                law that decides is `src/lib/inbox-return.ts`. */}
            {/* QC-1 — **THE X IS TOP RIGHT, WHICH IS WHERE A CLOSE LIVES.** It shipped on the left,
                in the slot a BACK chevron used to hold, and her report is the honest reading of
                that: a control in the back position reads as back however it is drawn. Compose
                takes the left slot; the order in the DOM is the order on the screen, so nothing
                here depends on a rule remembering to reverse it. */}
            <button type="button" className="inbox-compose" onClick={() => setComposing(true)}
              aria-label={INBOX_COMPOSE_LABEL} title={INBOX_COMPOSE_LABEL}>
              <PenSquare size={18} />
            </button>
            <h1 className="inbox-head__title">{INBOX_TITLE}</h1>
            <button type="button" className="inbox-head__close" onClick={goBack} aria-label={INBOX_CLOSE_LABEL} title={INBOX_CLOSE_LABEL}>
              <X size={28} strokeWidth={2.5} />
            </button>
          </header>

          {/* ITEM 7 — **THE FOLLOW-STRIP, WHICH ITEM 5 DELIBERATELY DID NOT BUILD.** Her mockup puts
              it across the top of the inbox and it reads the follow graph; a strip over no graph is
              a shell, so it waited for the graph rather than shipping as furniture. */}
          <FollowStrip onOpen={openThread} />

          {/* THE SEARCH BAR AND ITS ONE FILTER. Both are the same server read — see the law.
              The ROW is what puts the sliders glyph beside the bar rather than inside it. */}
          <div className="inbox-searchrow">
          <div className="inbox-search">
            <Search size={15} className="inbox-search__icon" aria-hidden="true" />
            <input
              className="inbox-search__input" value={q} placeholder={INBOX_SEARCH_COPY}
              aria-label={INBOX_SEARCH_COPY}
              onChange={(e) => { setQ(e.target.value); search(e.target.value, unreadOnly); }}
            />
            {q ? (
              <button type="button" className="inbox-search__clear" aria-label="Clear search"
                onClick={() => { setQ(""); void loadList("", unreadOnly); void loadPeople(""); }}><X size={14} /></button>
            ) : null}
          </div>
          {/* **THE FILTER IS A CONTROL BESIDE THE BAR, NOT A WORD INSIDE IT.** Her mockup draws the
              sliders glyph outside the search field, which is why it moved OUT of `.inbox-search`.
              The word it replaces is the accessible name and the title, so the catalog row is still
              read and a screen reader still hears "Unread" — the filter did not become quieter, it
              became her shape. `aria-pressed` is what says it is ON, exactly as before. */}
          <button type="button" aria-pressed={unreadOnly}
            className={unreadOnly ? "inbox-filter is-on" : "inbox-filter"}
            aria-label={INBOX_UNREAD_FILTER} title={INBOX_UNREAD_FILTER}
            onClick={() => { const next = !unreadOnly; setUnreadOnly(next); loadList(q, next); }}>
            <SlidersHorizontal size={18} />
          </button>
          </div>

          {/* An empty inbox and a search that found nothing are DIFFERENT FACTS, and telling her the
              first when the second is true reads as messages having gone missing.

              R84 — **AND A THIRD FACT SITS ABOVE BOTH OF THEM: THE LIST NEVER ARRIVED.** Held by
              POSITION (R65's ruling), because a check that only asks whether the sentence exists
              passes on a screen that prints both — and the search sentence is the worse of the two,
              since it names a term SHE typed as the reason her conversations are not on screen. */}
          {/* >>> HER CLAUSE: PEOPLE FIRST. <<< The ORDER is the whole of it, so this block is
              ABOVE the messages block by POSITION and the law says so too — a build that appended
              people under her conversations satisfies every presence check there is.

              The section DRAWS on every state but `hidden`, because a heading that vanishes on a
              slow read is a screen she cannot trust; and a people read that did not land may never
              take her conversations off the screen, which is why it has its own sentence rather
              than sharing `readNote`. */}
          {inboxSearchSections(q, people)[0] === "people" ? (
            <section className="inbox-people" aria-label={INBOX_PEOPLE_HEADING}>
              <h3 className="inbox-people__head">{INBOX_PEOPLE_HEADING}</h3>
              {inboxPeopleState(q, people) === "failed" ? (
                <p className="lr-empty">{peopleNote || ROOM_READ_FAILED}</p>
              ) : null}
              {inboxPeopleState(q, people) === "none" ? (
                <p className="lr-empty">{peopleHeld ? inboxPeopleHeld(peopleHeld) : inboxPeopleEmpty(q)}</p>
              ) : null}
              {inboxPeopleState(q, people) === "list" ? (
                <>
                  <ul className="lr-picker lr-picker--static">
                    {(people as Identity[]).map((x) => (
                      <li key={x.ref}>
                        {/* THE SAME DOOR HER CIRCLE TAP USES, so there is one answer to what
                            Message does — and it is the door `6aa2468a` repaired, which is what
                            makes a member who has never written a word reachable from here. */}
                        <button type="button" onClick={() => { void openMemberThread(String(x.ref)); }}>
                          <Avatar who={x} size="sm" />
                          <span>@{x.name}<OwnerCrown who={x} /></span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {peopleHeld ? <p className="lr-note">{inboxPeopleHeld(peopleHeld)}</p> : null}
                </>
              ) : null}
            </section>
          ) : null}

          {/* MESSAGES. Her split's second half, and it keeps its own heading only when the first
              half is on screen — a lone "Messages" heading over an unsearched inbox is a label for
              a thing that has no sibling. */}
          {inboxSearchSections(q, people).length > 1 ? (
            <h3 className="inbox-people__head">{INBOX_MESSAGES_HEADING}</h3>
          ) : null}
          {threads === null ? <p className="lr-empty">{readNote || ROOM_READ_FAILED}</p> : null}
          {threads !== null && !threads.length ? (
            <p className="lr-empty">{q.trim() ? inboxSearchEmpty(q) : EMPTY_INBOX}</p>
          ) : null}

          {/* L6(4) — **HER FOLLOW NOTE, ON BOTH VIEWPORTS.** A teaching, not a warning: it sits under
              the header where the screen is calm, rather than arriving as ITEM 9's refusal after she
              has chosen somebody and pressed a button. It is HELD BY POSITION above the rows and
              below the search, so it can never end up as a line she has to scroll past her
              conversations to find. The words are the room's own law, never a literal here — this is
              the one kind of copy this house keeps losing track of (14-B5's bell and mail). */}
          <p className="inbox-note">{INBOX_FOLLOW_NOTE}</p>

          {/* ITEM 5 (her `4`) — **THE UNBLOCK PATH'S DOOR, AND IT IS ALWAYS DRAWN.**
              Her sentence is *"unblock path in the same place"*. It cannot hang off the Block button:
              blocking closes the thread and takes that member out of her room, so the control leaves
              with the person it points at. It sits in the INBOX, which is the screen the block is
              performed from and the only one that survives it.

              **UNCONDITIONAL RATHER THAN CONDITIONAL ON A COUNT**, deliberately: a door that appears
              only once she has blocked somebody is a door she cannot find BEFORE she needs it, and a
              count would be a second read of a fact this screen does not otherwise need. The empty
              state is warm and says what the place is for (L2.7's ruling on the empty inbox). */}
          <p className="inbox-note inbox-note--blocked">
            <button type="button" className="lr-link" onClick={() => setBlockedOpen(true)}>
              <Ban size={13} />{BLOCKED_LIST_LABEL}
            </button>
          </p>
          {blockedOpen ? (
            <BlockedList onClose={() => setBlockedOpen(false)} onChanged={() => loadList()} />
          ) : null}

          <ul className="inbox-rows">
            {(threads || []).map((t) => {
              // WHOSE FACE IS ON THE ROW: the other person's, and in a group the first of them. Her
              // own is never the row's face — she knows who she is.
              //
              // That sentence was TRUE ABOVE THIS LINE AND FALSE ON IT until launch day: the
              // `|| t.participants[0]` fell back to the viewer whenever the counterpart's membership
              // row carried a `left_at`, which is her thread with David. `otherParticipant` cannot
              // return the viewer, so the law is now held by the predicate rather than by the remark.
              const other = otherParticipant(t.participants, me?.me.ref);
              return (
                <li key={t.id} className={t.unread ? "inbox-row is-unread" : "inbox-row"}>
                  <button type="button" className="inbox-row__open" onClick={() => openThread(t.id)}>
                    {/* GROUP IMAGES: the group picture sits where a DM shows the other face.
                        N4 (her queue item 8, 2026-08-11) — **AND THE FACE IS NOW THE SIZE THAT
                        PICTURE HAS ALWAYS BEEN.** This slot drew a 44px square for a group and a 36px
                        circle for a DM, so the list has been internally inconsistent all along; her
                        "size them up, consistent" clause is answered by the number this house already
                        chose for this position rather than by one picked by eye. */}
                    {t.image
                      ? <img className="inbox-row__groupimg" src={t.image} alt="" />
                      : other ? <Avatar who={other} size="lg" /> : null}
                    <span className="inbox-row__body">
                      <span className="inbox-row__top">
                        <strong className="inbox-row__who">{t.pinned ? <Pin size={11} aria-hidden="true" /> : null}{t.title}</strong>
                        <span className="lr-when">{ago(t.lastMessageAt)}</span>
                      </span>
                      <span className="inbox-row__preview">{t.preview}</span>
                    </span>
                    {t.unread ? <em className="lr-unread">{t.unread}</em> : null}
                  </button>
                  <button type="button" className="inbox-row__pin" aria-pressed={Boolean(t.pinned)}
                    aria-label={t.pinned ? INBOX_UNPIN_LABEL : INBOX_PIN_LABEL}
                    title={t.pinned ? INBOX_UNPIN_LABEL : INBOX_PIN_LABEL}
                    onClick={() => pin(t, !t.pinned)}>
                    {t.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                  </button>
                </li>
              );
            })}
          </ul>
          {composing ? (
            <ComposeMessage me={me?.me ?? null} onClose={() => setComposing(false)} onStarted={(id) => { setComposing(false); openThread(id); }} />
          ) : null}
        </>
      ) : (
        // L6(4) — **THE THREAD IS A COLUMN WITH A PINNED FOOT, AND IT NEEDED A NAME TO BE ONE.**
        // Her ruling: *"the compose/text box PINS to the bottom of the thread view, stationary, only
        // the MESSAGES scroll, the input never scrolls away."* The thread branch was a bare fragment,
        // so there was no element for that law to be about — the messages box carried a `46vh` cap
        // and the composer simply sat under whatever it left behind, which is why on a short thread
        // it floated mid-screen with the rest of the page empty below it. One wrapper, and the
        // frame's own `:has()` rule turns the thread into head / scrolling messages / stationary
        // foot. **THE LIST VIEW IS UNTOUCHED BY CONSTRUCTION**, which is what keeps her item 3's
        // frozen phone structure a property rather than a promise.
        <div className="inbox-thread">
          <div className="lr-thread-head">
            <button type="button" className="lr-link" onClick={() => { setOpen(""); setThread(null); loadList(); }}>
              <ArrowLeft size={14} />Inbox
            </button>
            {/* GROUP IMAGES: and at the top of the thread, her second named surface. */}
            {thread.image ? <img className="lr-thread-head__img" src={thread.image} alt="" /> : null}
            {/* DEFECT 2 — **THE CREATOR CAN NAME AND PICTURE HER GROUP, AND THE DOOR IS VISIBLE.**
                The feature shipped with no way in: only the creator may edit, and nothing on the
                thread said so. Her words are "tap the group name or a pencil", so it is BOTH — a
                title that is secretly a button is a control nobody finds, which is the same lesson
                the share icon taught. It asks `iCreated`, the predicate the ROUTE refuses on, so
                nobody is offered a door that answers 403 (R15). **It works on groups created before
                this shipped**, because `iCreated` is computed from `created_by`, which every group
                has always carried. */}
            {thread.group && thread.iCreated ? (
              <button type="button" className="lr-thread-head__title lr-thread-head__title--edit"
                onClick={() => setEditing(true)} aria-label={GROUP_EDIT_LABEL}>
                {thread.title}<Pencil size={13} aria-hidden="true" />
              </button>
            ) : (
              <strong className="lr-thread-head__title">{thread.title}</strong>
            )}
            <div className="lr-thread-head__acts">
              {/* LR18b(d) — HER CLAUSE 4: **the CREATOR adds.** The control asks the same predicate
                  the route refuses on, so a member is never offered a door that answers 403 (R15). */}
              {canManageGroup(thread) ? (
                <button type="button" className="lr-link" onClick={() => setAdding(true)} aria-label="Add someone"><UserPlus size={14} /></button>
              ) : null}
              {/* LR18b(c) — HER CLAUSE 4. **A GROUP IS THE ONE SHAPE S6-5's RULING DOES NOT COVER:**
                  blocking somebody does not take her out of a room with five other people in it. What
                  makes this control honest rather than the twin she killed is that it ASKS — it opens
                  a confirm and posts nothing — and it is drawn ONLY on a group, because a DM's way
                  out is BLOCK by her own word. */}
              {thread.group ? (
                <button type="button" className="lr-link" onClick={() => setLeaving(true)}
                  aria-label={LEAVE_GROUP_LABEL}><DoorOpen size={14} /></button>
              ) : null}
              {/* ITEM 1 — **THE CLOSE IS REACHABLE FROM INSIDE A THREAD TOO.** Her sentence is about
                  the inbox, not about the list view of it: a member reading a conversation is still
                  in the screen she says has no way out, and without this she must tap back to the
                  list first and then close, which is the two-tap escape she reported as *clicking
                  another button*. Same control, same law, same accessible name — one answer to what
                  closing the inbox means (R28/R29). */}
              <button type="button" className="lr-link inbox-head__close" onClick={goBack}
                aria-label={INBOX_CLOSE_LABEL} title={INBOX_CLOSE_LABEL}><X size={24} strokeWidth={2.5} /></button>
            </div>
          </div>
          {editing && thread ? (
            <GroupEditSheet thread={thread} onClose={() => setEditing(false)}
              onSaved={() => { setEditing(false); void openThread(thread.id); loadList(); }}
              onDeleted={() => { setEditing(false); setOpen(""); setThread(null); loadList(); }} />
          ) : null}

          {/* ITEM 5 — **THE GROUP MEMBER LIST IS A DROPDOWN NOW.** Her order: the names ran across the
              top and took too much room, so the top of a group thread shows the group NAME and one
              control labelled Group members, and the full list is one tap away. The list itself is
              unchanged — same rows, same follow tag, same block and remove controls — it is only
              folded. The count rides on the label because *how many* is the thing she wants at a
              glance; the names are what were costing the room. */}
          {thread.group ? (
            <div className="lr-groupmembers">
              <button
                type="button"
                className="lr-groupmembers__toggle"
                aria-expanded={membersOpen}
                onClick={() => setMembersOpen((v) => !v)}
              >
                <Users size={14} />
                {GROUP_MEMBERS_LABEL}
                <span className="lr-groupmembers__count">{currentMembers(thread.participants).length}</span>
                <ChevronDown size={14} className={membersOpen ? "lr-groupmembers__chev is-open" : "lr-groupmembers__chev"} />
              </button>
            </div>
          ) : null}
          {!thread.group ? null : membersOpen ? (
            <p className="lr-participants">
              {/* ITEM 8 (HER RULING): only CURRENT members. Everything a member who left CONTRIBUTED
                  stays in the thread untouched; what changes is membership, so her name comes out of
                  this list and nothing else about the conversation moves. */}
              {currentMembers(thread.participants).map((p) => (
                <span key={p.ref}>
                  <em className="lr-tag">@{p.name}<OwnerCrown who={p} /></em>
                  {/* ITEM 7: a DM is one of the surfaces her order names, and the people in a
                      conversation are the only member list this room ever renders. */}
                  <FollowTag who={p} />
                  {/* ITEM 3: asked of the room's own `canBlock` rather than of a hand-typed
                      `!p.owner`. This list had the test and the one-to-one list below did not, which
                      is how two members came to have a Block button pointed at the house. */}
                  {canBlock(p) ? <button type="button" className="lr-tiny" onClick={() => block(p.ref)} aria-label={`Block ${p.name}`}><Ban size={11} /></button> : null}
                  {/* LR18b(d) — **the CREATOR removes**, and never herself: her own way out is the
                      leave, behind the confirm that says what it costs. Like the leave, this control
                      only ASKS — the post lives inside the confirm. */}
                  {canManageGroup(thread) && String(p.ref) !== String(me?.me.ref) ? (
                    <button type="button" className="lr-tiny" onClick={() => setRemoving(p)}
                      aria-label={`${REMOVE_FROM_GROUP_LABEL}: @${p.name}`}><UserMinus size={11} /></button>
                  ) : null}
                </span>
              ))}
            </p>
          ) : null}
          {thread.group ? null : (
            <p className="lr-participants">
              {thread.participants.filter((p) => p.ref !== me?.me.ref).map((p) => (
                <span key={p.ref} className="lr-participants__one">
                  <FollowTag who={p} />
                  {/* ITEM 3: **THE CONTROL THAT MADE THE TWO LIVE BLOCKS ON HER SEAT.** The group list
                      above refused to draw it on an owner from the day it was written; this one, the
                      one-to-one, offered it — and a DM with the house is the only conversation most
                      members ever have with her. Same predicate now, both lists. */}
                  {canBlock(p) ? <button type="button" className="lr-link" onClick={() => block(p.ref)}><Ban size={13} />Block @{p.name}</button> : null}
                </span>
              ))}
            </p>
          )}

          {/* ITEM 10 — **ONE MARK, ON THE LAST OF HER MESSAGES THAT HAS BEEN READ.** Which message
              wears it is a LAW (`receiptMessageId`), not a loop here: "the last one" is exactly the
              kind of thing that survives a refactor backwards into "the first one" with nothing on
              screen to notice. The server decided WHO has read it — owners are never in that list
              and a held message's list is always empty — so this only draws. */}
          {/* C4 — the receipt is resolved ONCE for the whole thread. See the note on the map below. */}
          <div className="lr-messages" ref={logRef} onScroll={onLogScroll} data-receipt-at={receiptAt || undefined}>
            {/* ITEM 7 — **THE ROW MOVED OUT INTO `MessageRow` AND EVERY DECISION IN IT IS A PROP.**
                Her clause 4 asks for one shared component; measured first, this was already ONE
                render site serving DMs and groups alike (R19/R25 — *"a component to be EXTRACTED,
                not a duplication to be merged"*), so what the extraction buys is not consolidation,
                it is REACH: a hookless component in `src/components/` is one a FAST fixture CALLS
                and reads the tree back from (R7/R8), where a block inside this 1,200-line file could
                only ever be checked by regex over source.

                The media index is the index within the WHOLE strip and the viewer is handed the
                WHOLE strip — the images-only filter is deleted rather than adjusted, because it
                opened the wrong photograph whenever a video sat first in a message. */}
            {/* C4 — **HER FREEZE, AND IT WAS QUADRATIC.** `receiptMessageId(thread.messages)` walks the
                WHOLE thread backwards, and it was called INSIDE this map — once per row — so the cost of
                one render was messages x messages. **Elite 2026 holds 618 messages and 274 live
                members**, the biggest thread on this site, which is up to **381,924 array steps per
                KEYSTROKE** on top of 618 React elements: her text box freezing every time she types.

                It does not depend on `m`, so hoisting it is byte-identical in what it renders and
                takes the render from n-squared to n. Held by POSITION, because a later round moving it
                back inside reads as a tidy-up and has no symptom on any thread but hers. */}
            {/* PERF-1 — **THE FOUR HANDLERS ARE STABLE, AND THAT IS WHAT MAKES `memo(MessageRow)`
                REAL RATHER THAN DECORATIVE.** Three of them were INLINE ARROWS here, so all 698 rows
                in Elite 2026 received three new function identities on every keystroke and a memo
                comparison would have found them unequal every single time — a wrapper that reads as a
                complete repair and skips nothing (this house's most-paid shape, arriving as a
                performance fix). The per-row closure for media moved INSIDE the row, which is why
                `onOpenMedia` now takes the message.

                They are declared beside the state they touch and depend on NOTHING, because every
                value they read is a setter, and React guarantees those are stable. */}
            {/* IT #124 — **THE INDEX IS THE ONLY NEW THING HERE, AND IT IS STABLE**, so PERF-1's
                memo comparison is untouched: a row's distance from the newest message does not move
                while she types. `rowMediaIsEager` is the law and it lives in `src/livingRoom.js`,
                where a gate RUNS it over every position in a thread this repo does not contain. */}
            {/* IT #167 — **THE HISTORY ABOVE THIS PAGE IS OFFERED, NEVER SILENTLY DROPPED.** The
                Elite group had grown to 968 messages and the read had no limit, so it stopped
                opening on a phone at all. The newest page loads instantly now and everything older
                is one tap away, with the count on the button so she knows what she is asking for. */}
            {thread.hasMore ? (
              <button type="button" className="lr-thread__older" disabled={loadingOlder}
                onClick={async () => {
                  if (loadingOlder || !thread.messages.length) return;
                  setLoadingOlder(true);
                  try {
                    const before = thread.messages[0]?.createdAt;
                    const res = await fetch(`/api/living-room/thread/${thread.id}?before=${encodeURIComponent(String(before || ""))}`);
                    const data = await res.json().catch(() => null);
                    const older = data?.thread?.messages;
                    if (Array.isArray(older) && older.length) {
                      setThread((t) => (t ? {
                        ...t,
                        messages: [...older, ...t.messages],
                        hasMore: Boolean(data.thread.hasMore),
                        olderCount: Number(data.thread.olderCount) || 0,
                      } : t));
                    } else {
                      setThread((t) => (t ? { ...t, hasMore: false, olderCount: 0 } : t));
                    }
                  } catch { /* a failed page leaves the thread exactly as it was */ }
                  finally { setLoadingOlder(false); }
                }}>
                {loadingOlder ? "Loading…" : `Load earlier messages${thread.olderCount ? ` (${thread.olderCount})` : ""}`}
              </button>
            ) : null}
            {thread.messages.map((m, i) => (
              <MessageRow key={m.id} message={m}
                eagerMedia={rowMediaIsEager(i, thread.messages.length)}
                canLike={canActOn(m)}
                canDelete={canDelete(m, { ref: me?.me.ref, owner: me?.me.owner })}
                seen={m.id === receiptAt
                  ? receiptLabel(m.seen?.length || 0, { group: thread.group })
                  : null}
                onLike={like}
                onAskDelete={askDelete}
                onOpenMedia={openMedia}
                onOpenPost={openPost} />
            ))}
          </div>

          {/* LR16(a) — **THE PREVIEW BEFORE SEND, AND REMOVE-BEFORE-SEND.** Her order names both.
              Each preview carries its own remove, because "clear them all" is a different act from
              "not that one" and only one of them is what she meant. */}
          {draftMedia.length ? (
            <div className="lr-reply__tray">
              {draftMedia.map((m) => (
                <span key={m.id} className="lr-reply__chip">
                  {m.kind === "video"
                    ? <video className="lr-reply__thumb" src={m.url} preload="metadata" muted />
                    : <img className="lr-reply__thumb" src={m.url} alt="" />}
                  <button type="button" className="lr-reply__drop" aria-label="Remove this attachment"
                    onClick={() => setDraftMedia((prev) => prev.filter((x) => x.id !== m.id))}>x</button>
                </span>
              ))}
            </div>
          ) : null}
          <div className="lr-reply">
            <label className="lr-attach" aria-label="Attach a photo or video">
              <Paperclip size={18} />
              {/* R15: the picker offers exactly what the server accepts. `image/*,video/*` is
                  deliberately wide — a phone names its own containers and a narrow accept list is
                  how a member comes to be told her own camera roll is unsupported. */}
              <input type="file" accept="image/*,video/*" multiple
                onChange={(e) => { void pickMedia(e.target.files); e.target.value = ""; }} />
            </label>
            {/* MIC-1 (her order) — **THE INBOX COMPOSER GAINS THE MIC, IN DMs AND IN GROUP THREADS,
                REPLYING AND COMPOSING.** One box serves all of those, so it is one mic rather than
                four, and it is THE shared `MicButton` — never a private copy.
                >>> **AND THE CAP IS RE-APPLIED HERE, WHICH IS THE ONLY REAL DEFECT IN WIRING A MIC
                TO THIS BOX.** `MessageComposer`'s own note says a dictation insert is one of the
                paths that arrives at full length past `maxLength`, and it clamps in `onChange` for
                exactly that reason. The mic does NOT go through `onChange` — it writes the field's
                setter directly, so the cap rides as `max` — and it rides INTO THE SHARED HOOK rather
                than as a wrapper around this setter, because a clamp written at a call site is the
                copy the next call site forgets. Without it, speaking past 2,000 characters would
                compose a message the server refuses.
                NO `limits` ARE PASSED, AND THAT IS THE INSTRUCTION RATHER THAN AN OMISSION: this box
                already grows itself on every value change through `messageComposeLimits` (her third
                of the screen), so the mic's job here is the SCROLL half only. Handing it a second
                set of limits would be two grow laws on one box, which is the drift W4-7 named. */}
            <MicButton setValue={setBody} field={boxRef} max={me?.limits.messageChars || LIMITS.messageChars} />
            {/* HER ORDER (2026-08-07) — THE ONE GROWING COMPOSER. This was a single-row `<input>`
                with its own Enter-sends handler; it is the shared box now, so the cap, the growth,
                the red line and Enter-makes-a-newline are the same here, in a group thread, and in
                the Living Room's comment box, by EXISTING rather than by three edits. */}
            <MessageComposer
              inputRef={boxRef} value={body} placeholder="Write a message"
              max={me?.limits.messageChars || LIMITS.messageChars}
              onChange={(next, caret) => { setBody(next); picker.look(next, caret); }}
              onBlur={picker.dismiss}
              // HER CLAUSE 4 — **THE GROWN BOX PUSHES THE THREAD UP, IT NEVER COVERS IT.** Growing
              // the composer shrinks the log above it, and a log that keeps its old `scrollTop`
              // slides its newest message up out of the clip: covered by another route. `toLatest`
              // is the surface's OWN law and honours `stickToLatest`, so a member deliberately
              // reading history is still not yanked back down (S6-1).
              onResized={() => toLatest()}
              after={(
                <MentionPicker anchor={boxRef} people={picker.people} onPick={(name) => {
                  const next = picker.choose(body, name);
                  setBody(next.text);
                  requestAnimationFrame(() => boxRef.current?.setSelectionRange(next.caret, next.caret));
                }} />
              )}
            />
            <button type="button" className={sending ? "lr-send is-sending" : "lr-send"} onClick={send}
              disabled={sending} aria-busy={sending} aria-label="Send"><Send size={18} /></button>
          </div>
        </div>
      )}
      {status ? <p className="lr-status">{status}</p> : null}
      {/* LR16(b) — THE ROUND-NINE LIGHTBOX, the room's own `ImageViewer`, not a second one built for
          the inbox. Swipe, the arrow keys, the counter and Download all come with it. */}
      {viewing ? <ImageViewer items={viewing.items} index={viewing.at} onClose={() => setViewing(null)} /> : null}
      {/* LR16-e — the room's ONE confirm, on its third kind. It re-opens the thread rather than
          splicing the row out here: the server decides what a deleted message leaves behind (a
          tombstone, and no media), and a client that painted its own answer would be a second one. */}
      {deleting ? (
        <ConfirmDelete itemKind="message" id={deleting}
          onClose={() => setDeleting("")} onDone={() => { setDeleting(""); if (open) openThread(open); }} />
      ) : null}
      {adding && open ? <AddToThread me={me?.me ?? null} threadId={open} onClose={() => setAdding(false)} onDone={(t) => { setThread(t); setAdding(false); }} /> : null}
      {/* LR18b(c) — the ask that separates this control from the one S6-5 killed. It closes the
          thread and reloads the list only on a leave that REALLY happened, which is the half a route
          answering `{ ok: true, left: false }` makes possible to get wrong (R39/R83/D-1). */}
      {leaving && open && thread ? (
        <ConfirmLeaveGroup threadId={open} title={thread.title} onClose={() => setLeaving(false)}
          onLeft={() => { setLeaving(false); setOpen(""); setThread(null); loadList(); }} />
      ) : null}
      {/* LR18b(d) — the same ask one seat over. The route hands back the whole thread, so the
          participant line redraws from the SERVER's answer rather than from a guess here (R39). */}
      {removing && open ? (
        <ConfirmRemoveFromGroup threadId={open} who={removing} onClose={() => setRemoving(null)}
          onRemoved={(t) => { setRemoving(null); if (t) setThread(t); }} />
      ) : null}
    </div>
    </LivingRoomMe.Provider>
  );
}

// ITEM 7 — **THE FOLLOW-STRIP.** The faces and @names of the people she follows, with a dot where
// there is something unread from them, across the top of her inbox, exactly as her mockup has it.
//
// It reads `/api/living-room/following`, which IS the follow graph read back — there is no second
// definition of "who she follows" anywhere in the house. It re-reads on the FOLLOW event, so
// following somebody in the thread underneath adds them to the strip above without a reload.
//
// **A TAP OPENS THE CONVERSATION WITH THAT PERSON**, which is `ensureThread` — idempotent, so it is
// "open" for somebody she has written to and "start" for somebody she has not, and the strip does
// not have to know which.
//
// LR18b(b) — **AND HER GROUPS ARE IN IT NOW, FIRST.** Not by taste: a group is the only thing on this
// rail with no other door. Every person here is one tap from every @name the room prints, while a
// group thread is reachable from the inbox list and from this strip and nowhere else.
//
// **THE GROUPS ARE READ UNFILTERED, ON PURPOSE.** The parent's `threads` state is whatever her SEARCH
// last returned, so drawing the circle from it would make a group VANISH out of her circle because
// she typed in the box below it. This reads the same route with no query — one answer key (the
// route), one extra round trip per inbox open, and a rail that does not react to a search of her
// messages.
function FollowStrip({ onOpen }: { onOpen: (threadId: string) => void }) {
  const [people, setPeople] = useState<Array<Identity & { unread: number }> | null>(null);
  const [groups, setGroups] = useState<ThreadRow[]>([]);
  const [note, setNote] = useState("");
  // The READ's sentence and the TAP's are two facts and are kept apart, as they are in the inbox.
  const [tapNote, setTapNote] = useState("");
  const load = useCallback(async () => {
    const d = await json("/api/living-room/following");
    // R84 — **THIS COMPONENT'S OWN COMMENT NAMED THE DEFECT AND THE CODE DID IT ANYWAY**: "a strip
    // with nothing in it reads as a thing that failed to load", and a strip that failed to load read
    // as somebody who follows nobody. Two facts, two sentences.
    const list = roomItems(d, "following");
    setPeople(list);
    setNote(list ? "" : roomNote(d, ROOM_READ_FAILED));
  }, []);
  const loadGroups = useCallback(async () => {
    const d = await json("/api/living-room/inbox");
    // R84/R53's third state, one rail over: a read that did not land is NOT "she is in no groups".
    // The rail keeps whatever it last drew rather than emptying her circle on a dropped request —
    // and the people half already carries the sentence for a strip that failed, so this does not
    // add a second one about the same rail.
    const list = roomItems(d, "threads");
    if (list) setGroups(circleGroups(list));
  }, []);
  useEffect(() => { load(); loadGroups(); }, [load, loadGroups]);
  useEffect(() => {
    const heard = () => { load(); };
    window.addEventListener(FOLLOW_EVENT, heard);
    return () => window.removeEventListener(FOLLOW_EVENT, heard);
  }, [load]);

  // The empty state is a SENTENCE, not an empty rail: a strip with nothing in it reads as a thing
  // that failed to load. R84: and the thing that failed to load says so, above the empty state.
  //
  // **A GROUP KEEPS THE RAIL ALIVE.** Both branches below ask about the PEOPLE, so a member who
  // follows nobody and is in three groups would have read *"Follow somebody..."* over a circle that
  // really has three things in it — the empty state describing the half it was written about.
  if (people === null && !groups.length) return <p className="inbox-strip__empty">{note || ROOM_READ_FAILED}</p>;
  if (!(people || []).length && !groups.length) return <p className="inbox-strip__empty">{EMPTY_FOLLOW_STRIP}</p>;
  return (
    <>
    <div className="inbox-strip" role="list" aria-label={CIRCLE_STRIP_LABEL}>
      {groups.map((t) => {
        const badge = groupMedallion(t);
        return (
          <button
            key={`g${t.id}`}
            type="button"
            role="listitem"
            className={t.unread ? "inbox-strip__person has-unread" : "inbox-strip__person"}
            onClick={() => { setTapNote(""); onOpen(String(t.id)); }}
          >
            <span className="inbox-strip__face inbox-strip__face--group">
              {/* The medallion is a LAW's answer rather than a `title[0]` here: an initial taken from
                  a title the HOUSE composed is the first letter of somebody's name wearing a group's
                  badge, so an unnamed group takes the glyph and claims nothing. */}
              <span className="inbox-strip__medallion" aria-hidden="true">
                {/* Her picture outranks the initial and the glyph: a group that HAS a face shows it. */}
                {t.image ? <img className="inbox-strip__groupimg" src={t.image} alt="" />
                  : badge.kind === "initial" ? badge.text : <Users size={18} />}
              </span>
              {t.unread ? <em className="inbox-strip__dot" aria-label={`${t.unread} unread`} /> : null}
            </span>
            <span className="inbox-strip__name">{t.title}</span>
            <span className="visually-hidden">{CIRCLE_GROUP_LABEL}</span>
          </button>
        );
      })}
      {(people || []).map((who) => (
        <button
          key={who.ref}
          type="button"
          role="listitem"
          className={who.unread ? "inbox-strip__person has-unread" : "inbox-strip__person"}
          onClick={async () => {
            const d = await postJson("/api/living-room/thread", { ref: who.ref });
            // R84 — **ITEM 9's OWN GATE SENTENCE HAD NO WAY OUT OF THIS TAP.** A refused open did
            // nothing at all: no conversation, no word, a face that does not answer. The route
            // composes the follow refusal, the strike notice and the room's one unreachable
            // sentence, and all three landed here and stopped.
            if (d?.ok && d.threadId) { setTapNote(""); onOpen(String(d.threadId)); return; }
            setTapNote(roomNote(d));
          }}
        >
          <span className="inbox-strip__face">
            <Avatar who={who} size="md" />
            {who.unread ? <em className="inbox-strip__dot" aria-label={`${who.unread} unread`} /> : null}
          </span>
          <span className="inbox-strip__name">@{who.name}<OwnerCrown who={who} /></span>
        </button>
      ))}
    </div>
    {tapNote ? <p className="lr-status">{tapNote}</p> : null}
    </>
  );
}

// ITEM 5 — COMPOSE. It reuses the room's OWN people picker (`/api/living-room/mentions`), which is
// built from participation on the server: there is no client-side directory of members to leak, and
// starting a conversation cannot reach anybody the @picker would not have offered.
//
// ITEM 9 — **THE PICKER NARROWS TO WHAT THE SERVER WILL ACCEPT**, which is R15's law (a picker that
// offers what the gate refuses costs her the whole action before she is told no). It narrows on the
// identity's OWN `following` flag, so there is no second definition of who she follows anywhere —
// ITEM 7 already carries the answer on every member this route draws. The server refuses regardless:
// the picker is a shortcut and the gate is the server's, always.
// LR18b(a) — **HER CLAUSE 1: SHE PICKS MORE THAN ONE, AND SHE NAMES IT.** The picker is unchanged in
// what it OFFERS; what changed is that a row is a TOGGLE rather than a send. The one thing that made
// this a round rather than an edit is that the name is not decoration: 8a shipped the route's half
// with the requirement deliberately left OFF, on the argument that no compose sent a title, so this
// surface is where the requirement lives — and it is asked of `groupComposeViolations` rather than of
// a `length > 2` here, because a second definition of what a group is is exactly the answer key 8a
// spent its round abolishing on the server.
// ITEM 3 — **WOULD THE DOOR REFUSE THIS ROW FOR NOT-FOLLOWING? THE DOOR'S OWN LAW, RUN, RATHER THAN
// A SECOND SPELLING OF IT.** Both pickers read `p.following` directly, which is the follow rule copied
// onto a surface — so the moment her seat stopped needing a follow, they were the two places still
// enforcing the old rule and her compose could have found nobody. `messageGateViolations` is exactly
// what `/thread/start` and `/thread/:id/add` ask, given the facts a row already carries, so a list can
// only ever be what those doors would accept (R15/R28/R29).
//
// **`threadExists` IS FALSE ON PURPOSE, IN BOTH.** Each of these pickers opens a conversation with
// somebody who is not in one — the thread `AddToThread` is adding to is not the one being opened with
// this person, which is the argument its own route is written on.
//
// It takes the viewer's whole identity rather than a ref: the only new fact either surface needs is
// `owner`, and it is already on the identity every other control in this file reads.
function pickerRefuses(me: Identity | null, p: Identity) {
  return messageGateViolations({
    viewer: {
      ref: String(me?.ref ?? ""),
      owner: Boolean(me?.owner),
      following: new Set(p.following ? [String(p.ref)] : []),
    },
    targetRefs: [String(p.ref)],
  }).includes("not-following");
}

function ComposeMessage({ me, onClose, onStarted }: {
  me: Identity | null; onClose: () => void; onStarted: (threadId: string) => void;
}) {
  const [q, setQ] = useState("");
  const [people, setPeople] = useState<Identity[]>([]);
  const [held, setHeld] = useState(0);
  const [readNote, setReadNote] = useState("");
  const [status, setStatus] = useState("");
  const [picked, setPicked] = useState<Identity[]>([]);
  const [name, setName] = useState("");
  // The chosen picture, cropped square before a byte leaves the device by the SAME function a
  // profile photo uses. It is held until the thread exists, because a group image is keyed on a
  // thread id and there is no thread until she presses Start.
  const [pic, setPic] = useState<{ data: string; contentType: string; width: number; height: number } | null>(null);
  const [picPreview, setPicPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // D-5's ruling, on the one control here that writes: a `useState` guard is read from the render
  // that BOUND the handler, so three presses inside one frame all see `false`. The state survives as
  // what the button WEARS; the ref is what decides.
  const inFlight = useRef(false);
  useEffect(() => {
    const t = window.setTimeout(async () => {
      const d = await json(`/api/living-room/mentions?q=${encodeURIComponent(q)}`);
      // R84: a read that did not land is not a room with nobody in it. The hint under this box is a
      // statement about WHO IS HERE, so printing it over a failed lookup tells her a member she
      // knows exists does not.
      const found = roomItems(d, "people");
      setReadNote(found ? "" : roomNote(d, ROOM_READ_FAILED));
      const all: Identity[] = found || [];
      const mine = all.filter((p) => !pickerRefuses(me, p));
      setPeople(mine);
      // **THE COUNT IS WHY THIS IS NOT JUST A FILTER.** She typed a real member's name, the room
      // found her, and the list came back empty — an empty list under a name that exists reads as
      // broken rather than as a rule, so the two empty states say different things.
      setHeld(all.length - mine.length);
    }, 150);
    return () => window.clearTimeout(t);
    // ITEM 3: the two facts the filter above reads, by VALUE rather than by object identity — `me`
    // arrives from a fetch and a new object every render would re-run this lookup on every keystroke
    // twice over.
  }, [q, me?.ref, me?.owner]);

  const refs = composePicks(picked.map((p) => p.ref));
  const isGroup = composeIsGroup(refs);
  // R15 again, one control smaller: the twelfth seat is the viewer's own, so a row that cannot be
  // added is not offered as addable. The law still refuses on submit — this only stops her finding
  // out by pressing.
  const atCapacity = refs.length + 1 >= MAX_THREAD_MEMBERS;
  const toggle = (p: Identity) => setPicked((was) =>
    was.some((x) => String(x.ref) === String(p.ref))
      ? was.filter((x) => String(x.ref) !== String(p.ref))
      : [...was, p]);

  const pickPicture = async (file: File | null) => {
    if (!file) return;
    setStatus("");
    // **THE HONESTY LAW: A FAILED PICK SAYS SO, IN PLAIN WORDS.** It used to leave a broken icon
    // and a submit that refused forever — which is what she reported. Every path below either
    // produces a picture she can see or a sentence she can read.
    // The picker refuses in the ROUTE'S own words — one law, both ends (R28/R29).
    // The NAME answers when the browser will not — an iPhone HEIC arrives with no type at all.
    const type = photoTypeFor(file.name, file.type);
    const refused = threadImageRefusal({ bytes: file.size, contentType: type });
    if (refused) { setStatus(refused); return; }
    try {
      // The crop is attempted for EVERY format — see `GroupEditSheet`'s note: the server cannot
      // convert a HEIC (no HEVC decoding plugin, measured), and the browser her photo comes from can.
      const ready = await prepareAvatarForUpload(file);
      if (!ready || !ready.base64) { setStatus(UNREADABLE_PICTURE); return; }
      setPic({ data: ready.base64, contentType: ready.contentType, width: ready.width, height: ready.height });
      setPicPreview(ready.dataUrl);
    } catch {
      setStatus(UNREADABLE_PICTURE);
    }
  };

  const start = async () => {
    if (inFlight.current) return;
    // R15 — the surface refuses exactly what the route would, and refuses it BEFORE the round trip,
    // because the alternative is her typing a name and picking six people to be told no afterwards.
    // The server refuses regardless: this is a shortcut, and the gate is always the server's.
    const bad = groupComposeViolations({ refs, name });
    if (bad.length) { setStatus(groupComposeRefusal(bad)); return; }
    inFlight.current = true;
    setBusy(true);
    setStatus("");
    try {
      const d = await postJson("/api/living-room/thread", { refs, title: name.trim() });
      // "Blocked" and "not here" answer the same sentence, which is LR1's ruling 2 and the reason
      // this reads the server's message rather than composing one of its own. The name refusals
      // arrive the same way, so a name the CONTENT GATE turned down says what the room says and not
      // what the shape law would have guessed.
      if (d?.ok && d.threadId) {
        // **THE PICTURE IS A SECOND WRITE AND THAT IS DELIBERATE**, not a shortcut: a group image is
        // keyed on a thread id, so there is nothing to attach it to until the thread exists. It is
        // NOT awaited into her success — the group was made, and a picture that failed must not read
        // as a group that did not (R18). She can set it from the edit door either way.
        if (pic) void postJson("/api/living-room/group-image", { threadId: String(d.threadId), ...pic });
        onStarted(String(d.threadId));
      }
      else setStatus(d?.message || "That did not work.");
    } finally {
      // D-5's own reason: a stuck flag here is a permanently dead button with her whole selection
      // and her typed name still sitting behind it.
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <Sheet label={INBOX_COMPOSE_LABEL} onClose={onClose} className="lr-modal--pick">
      <h2>{INBOX_COMPOSE_LABEL}</h2>
      <input className="lr-input" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Who are you writing to" aria-label="Who are you writing to" />
      {/* The chips are the only thing on this sheet that says a second tap was possible at all, so
          they render the moment there is one — and each one is its own control, because a selection
          she cannot undo is a sheet she has to close and reopen. */}
      {picked.length ? (
        <ul className="compose-picked" aria-label={COMPOSE_SELECTED_LABEL}>
          {picked.map((p) => (
            <li key={p.ref}>
              <Avatar who={p} size="sm" />
              {/* ITEM 11's law caught this chip on the first gate run, exactly as built: a chip is a
                  BYLINE and not an action sentence, so the crown is part of the name here too. */}
              <span>@{p.name}<OwnerCrown who={p} /></span>
              <button type="button" className="compose-picked__x" onClick={() => toggle(p)}
                aria-label={`${COMPOSE_REMOVE_LABEL} @${p.name}`}><X size={12} /></button>
            </li>
          ))}
        </ul>
      ) : null}
      {!people.length ? <p className="lr-note">{readNote || (held ? COMPOSE_FOLLOW_HINT : INBOX_COMPOSE_HINT)}</p> : null}
      {people.length && !picked.length ? <p className="lr-note">{COMPOSE_MULTI_HINT}</p> : null}
      {/* **AN EMPTY LIST BOX IS NOT AN EMPTY STATE, IT IS A BROKEN CONTROL.** The capture found this
          one: `.lr-picker` carries its own border, so with nothing in it the sheet drew a bare gold
          bar under the sentence explaining why there is nothing. It existed before this item and
          this item is what makes the state common, so it goes now. */}
      {people.length ? (
      <ul className="lr-picker lr-picker--static">
        {people.map((p) => {
          const on = picked.some((x) => String(x.ref) === String(p.ref));
          return (
          <li key={p.ref}>
            {/* `aria-pressed` is what makes a toggle a toggle to anything not looking at the tint. */}
            <button type="button" aria-pressed={on} disabled={!on && atCapacity}
              className={on ? "is-picked" : ""} onClick={() => toggle(p)}>
              <Avatar who={p} size="sm" />
              <span>@{p.name}<OwnerCrown who={p} /></span>
            </button>
          </li>
          );
        })}
      </ul>
      ) : null}
      {/* HER CLAUSE 1's REQUIREMENT, and it appears exactly when it binds. A name field over a
          one-to-one would be asking her to title a conversation with one person; the field arriving
          on the second pick is what teaches the rule without a sentence about it. */}
      {isGroup ? (
        <>
          <label className="lr-field"><span>{COMPOSE_GROUP_NAME_LABEL}</span>
            <input className="lr-input" value={name} onChange={(e) => setName(e.target.value)}
              maxLength={GROUP_NAME_MAX} aria-label={COMPOSE_GROUP_NAME_LABEL} /></label>
          {/* **HER PICTURE, FROM THE START.** It shipped as an edit-only capability, so a member
              making a group had nowhere to add one and had to go back in afterwards — which is what
              she reported. It sits in the SAME condition as the name, so the two can never appear
              apart, and it is OPTIONAL: no picture is the default look, unchanged. */}
          <div className="lr-field"><span>{GROUP_PICTURE_LABEL}</span>
            <div className="lr-groupedit__pic">
              {picPreview
                ? <img className="lr-groupedit__preview" src={picPreview} alt="" />
                : <span className="lr-groupedit__none" aria-hidden="true" />}
              <label className="lr-groupedit__pick">
                {GROUP_PICTURE_PICK}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
                  aria-label={GROUP_PICTURE_PICK}
                  onChange={(e) => { void pickPicture(e.target.files?.[0] || null); }} />
              </label>
            </div>
          </div>
        </>
      ) : null}
      {status ? <p className="lr-status">{status}</p> : null}
      {picked.length ? (
        <button type="button" className="button" disabled={busy} onClick={start}>
          {busy ? COMPOSE_STARTING_LABEL : COMPOSE_START_LABEL}
        </button>
      ) : null}
    </Sheet>
  );
}

// ITEM 9 narrows this one for the same reason and by the same law: adding somebody to a group IS
// starting to message them, and a group is where the follow gate would otherwise be routed around.
//
// **ITEM 3 — THIS IS THE SECOND PICKER ON THAT DOOR AND THE FIXTURE IS WHAT FOUND IT.** `handleAddToThread`
// asks `messageGateViolations` exactly as `thread/start` does, so the owner's exemption reaches it —
// and this list would have gone on hiding every non-follower from her, which is R15 pointed the wrong
// way: the gate accepts and the surface refuses, with no error state for her to notice. Same law, run
// the same way, in both pickers.
function AddToThread({ me, threadId, onClose, onDone }: {
  me: Identity | null; threadId: string; onClose: () => void; onDone: (t: any) => void;
}) {
  const [q, setQ] = useState("");
  const [people, setPeople] = useState<Identity[]>([]);
  const [held, setHeld] = useState(0);
  const [readNote, setReadNote] = useState("");
  const [status, setStatus] = useState("");
  useEffect(() => {
    const t = window.setTimeout(async () => {
      const d = await json(`/api/living-room/mentions?q=${encodeURIComponent(q)}&threadId=${threadId}`);
      const found = roomItems(d, "people");
      setReadNote(found ? "" : roomNote(d, ROOM_READ_FAILED));
      const all: Identity[] = found || [];
      // The people ALREADY in this thread come back from this route too and she does not follow all
      // of them — but they are not being added, so they are not the ones this hint is about.
      const mine = all.filter((p) => !pickerRefuses(me, p));
      setPeople(mine);
      setHeld(all.length - mine.length);
    }, 150);
    return () => window.clearTimeout(t);
  }, [q, threadId, me?.ref, me?.owner]);
  return (
    <Sheet label="Add to this conversation" onClose={onClose} className="lr-modal--pick">
      <h2>Add someone</h2>
      <input className="lr-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Room name" aria-label="Search by room name" />
      {!people.length && readNote ? <p className="lr-note">{readNote}</p> : null}
      {!people.length && !readNote && held ? <p className="lr-note">{COMPOSE_FOLLOW_HINT}</p> : null}
      {people.length ? (
      <ul className="lr-picker lr-picker--static">
        {people.map((p) => (
          <li key={p.ref}>
            <button type="button" onClick={async () => {
              const d = await postJson(`/api/living-room/thread/${threadId}/add`, { refs: [p.ref] });
              if (d?.ok) onDone(d.thread); else setStatus(d?.message || "That did not work.");
            }}>
              <span>@{p.name}<OwnerCrown who={p} /></span>
            </button>
          </li>
        ))}
      </ul>
      ) : null}
      {status ? <p className="lr-status">{status}</p> : null}
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// LR1-14 — SHARING A SPELL, and the three doors another member takes it home through.
// ---------------------------------------------------------------------------
export function ShareSpell({ onClose, onShared }: { onClose: () => void; onShared: () => void }) {
  const [f, setF] = useState({ title: "", intention: "", materials: "", steps: "", notes: "" });
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  return (
    <Sheet label={SHARE_SPELL_LABEL} onClose={onClose} className="lr-modal--spell">
      <h2>{SHARE_SPELL_LABEL}</h2>
      <p className="lr-note">Yours, in your own words. It goes up credited to your room name, and it stays credited to you.</p>
      <label className="lr-field"><span>What is it called</span>
        <input className="lr-input" value={f.title} onChange={set("title")} maxLength={120} /></label>
      <label className="lr-field"><span>What it is for</span>
        <textarea rows={2} value={f.intention} onChange={set("intention")} /></label>
      <label className="lr-field"><span>What you need</span>
        <textarea rows={3} value={f.materials} onChange={set("materials")} /></label>
      <label className="lr-field"><span>How it is done</span>
        <textarea rows={5} value={f.steps} onChange={set("steps")} /></label>
      <label className="lr-field"><span>Anything else</span>
        <textarea rows={2} value={f.notes} onChange={set("notes")} /></label>
      {status ? <p className="lr-status">{status}</p> : null}
      <button type="button" className="button" disabled={busy} onClick={async () => {
        setBusy(true);
        const d = await postJson("/api/living-room/spell", f);
        setBusy(false);
        if (!d?.ok) { setStatus(d?.message || "That did not send."); return; }
        onShared();
        onClose();
      }}>{busy ? "Sharing..." : "Share it"}</button>
    </Sheet>
  );
}

// THE WORKING, AS ANOTHER MEMBER SEES IT. The byline is not decoration: it is what the author gets
// in exchange for giving her working away, and it rides into the print sheet, the journal save and
// the cauldron send because all four read `spellPlainText`.
export function SpellCard({
  spell, author, actions, onUsed,
}: { spell: Spell; author: Identity; actions: Array<{ key: string; label: string }>; onUsed: (s: Spell) => void }) {
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState("");

  const use = async (action: string) => {
    setBusy(action);
    const d = await postJson("/api/living-room/spell/use", { spellId: spell.id, action });
    setBusy("");
    if (!d?.ok) { setStatus(d?.message || "That did not work."); return; }
    if (action === "print") printSheet(d.text, d.byline);
    else setStatus(action === "journal" ? "Saved to your Journal." : "Sent to your Cauldron.");
    if (d.spell) onUsed(d.spell);
  };

  return (
    <div className="lr-spell">
      <p className="lr-spell__eyebrow"><Sparkles size={12} />A spell</p>
      {/* **`h2` AND NOT `h3` (2026-08-14), AND HER OPEN-BY-DEFAULT RULING IS WHAT MADE IT VISIBLE.**
          A post draws no heading of its own, so this is the first heading under the room's `<h1>` —
          and at `h3` the outline jumped a level, which a screen reader reads as a missing section.
          The defect was always reachable; before her ruling it needed somebody to open a post
          carrying a shared spell, and now it is on the page on arrival. **The class carries every
          pixel of the styling, so nothing on screen moves** (R44's own repair pattern). */}
      <h2 className="lr-spell__title">{spell.title}</h2>
      <p className="lr-spell__by">by {spellByline(author)}</p>
      {spell.intention ? <p className="lr-spell__line"><span>For</span>{spell.intention}</p> : null}
      {spell.materials ? <p className="lr-spell__line"><span>You need</span>{spell.materials}</p> : null}
      {spell.steps ? <p className="lr-spell__line"><span>How</span>{spell.steps}</p> : null}
      {spell.notes ? <p className="lr-spell__line"><span>Notes</span>{spell.notes}</p> : null}
      <div className="lr-spell__acts">
        {actions.map((a) => (
          <button key={a.key} type="button" className="lr-chipbtn" disabled={busy === a.key} onClick={() => use(a.key)}>
            {a.key === "print" ? <Printer size={13} /> : a.key === "journal" ? <Flag size={13} /> : <Sparkles size={13} />}
            {a.label}
          </button>
        ))}
      </div>
      {/* HER TRUST LAW, IN WORDS: the number is MEMBERS, not taps. */}
      <p className="lr-spell__uses">{spellUseSummary(spell.counts)}</p>
      {status ? <p className="lr-status">{status}</p> : null}
    </div>
  );
}

// The print sheet. Opened as its own document so the room's dark chrome never lands on paper, and so
// the byline is the first thing under the title — a printout that loses the credit would hand a
// member's working to somebody else's paper unattributed.
function printSheet(text: string, byline: string) {
  const w = window.open("", "_blank", "width=720,height=900");
  if (!w) return;
  const doc = w.document;
  doc.title = text.split("\n")[0] || "A spell";
  const style = doc.createElement("style");
  style.textContent = `body{font:15px/1.65 Georgia,serif;color:#111;margin:48px;max-width:640px}
    h1{font-size:22px;margin:0 0 4px}.by{color:#7a6320;font-style:italic;margin:0 0 24px}
    pre{white-space:pre-wrap;font:inherit;margin:0}`;
  doc.head.appendChild(style);
  const lines = text.split("\n");
  const h1 = doc.createElement("h1");
  h1.textContent = lines[0] || "A spell";
  const by = doc.createElement("p");
  by.className = "by";
  by.textContent = `Shared by ${byline}`;
  const pre = doc.createElement("pre");
  // Elements and textContent, never innerHTML: this is member-authored text.
  pre.textContent = lines.slice(1).join("\n").replace(/^\s*Shared by [^\n]*\n?/, "").trim();
  doc.body.append(h1, by, pre);
  w.focus();
  w.print();
}

export { printSheet, spellPlainText };
